import * as THREE from 'https://esm.sh/three@0.160.1';
import { GLTFLoader } from 'https://esm.sh/three@0.160.1/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'https://esm.sh/three@0.160.1/examples/jsm/loaders/DRACOLoader.js';
import { OrbitControls } from 'https://esm.sh/three@0.160.1/examples/jsm/controls/OrbitControls.js';
import { fabric } from 'https://esm.sh/fabric@5.3.0';

const viewers = new WeakMap();
const editorBySection = new WeakMap();
const initializedTemplatePickers = new WeakSet();
const customizerTours = new WeakMap();
const DEFAULT_TEXTURE_WIDTH = 2976;
const DEFAULT_TEXTURE_HEIGHT = 2622;
const DEFAULT_PHYSICAL_WIDTH_MM = 252.11;
const DEFAULT_PHYSICAL_HEIGHT_MM = 222.11;
const MAX_SOURCE_IMAGES = 20;
const MAX_SINGLE_IMAGE_BYTES = 30 * 1024 * 1024;
const MAX_TOTAL_IMAGE_BYTES = 100 * 1024 * 1024;
const LOCAL_DRAFT_DB_NAME = 'mangorace-customizer-local-drafts';
const LOCAL_DRAFT_STORE_NAME = 'drafts';
const LOCAL_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const LOCAL_DRAFT_HANDOFF_STORAGE_KEY = 'mangorace-customizer-pending-handoff';
const CUSTOMIZER_TOUR_STORAGE_KEY = 'mangorace-customizer-guided-tour-v1';
const FINISH_LABELS = {
  matte: 'Matte',
  gloss: 'Gloss'
};

function getArtworkTags(category) {
  const tags = String(category || '').split('/').map((tag) => tag.trim()).filter(Boolean);
  const normalized = tags.map((tag) => tag.toLowerCase());
  const usage = normalized.includes('mix & match') ? 'mix-match' : normalized.includes('full wrap') ? 'full-wrap' : '';
  return { usage, topics: tags.filter((tag) => !['mix & match', 'full wrap'].includes(tag.toLowerCase())) };
}

function getLibraryArtworkOptions(usage, libraryId) {
  return usage === 'mix-match'
    ? { kind: 'library-element', libraryId, layout: usage }
    : { kind: 'library', replaceKind: 'library', libraryId, layout: usage };
}

function findArtworkBounds(pixels, width, height, padding = 8) {
  let left = width, top = height, right = -1, bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (pixels[(y * width + x) * 4 + 3] <= 16) continue;
      left = Math.min(left, x); top = Math.min(top, y);
      right = Math.max(right, x); bottom = Math.max(bottom, y);
    }
  }
  if (right < left) return null;
  left = Math.max(0, left - padding); top = Math.max(0, top - padding);
  right = Math.min(width - 1, right + padding); bottom = Math.min(height - 1, bottom + padding);
  return { cropX: left, cropY: top, width: right - left + 1, height: bottom - top + 1 };
}

async function prepareLibraryArtworkFile(blob, name, usage, preview = false) {
  const original = new File([blob], name, { type: blob.type });
  if (usage !== 'mix-match' || typeof createImageBitmap !== 'function') return original;
  let bitmap;
  try {
    bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width; canvas.height = bitmap.height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(bitmap, 0, 0);
    const bounds = findArtworkBounds(context.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height, preview ? Math.max(1, Math.round(8 * canvas.width / DEFAULT_TEXTURE_WIDTH)) : 8);
    if (!bounds) return original;
    canvas.width = bounds.width; canvas.height = bounds.height;
    context.drawImage(bitmap, bounds.cropX, bounds.cropY, bounds.width, bounds.height, 0, 0, bounds.width, bounds.height);
    const cropped = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    canvas.width = 0; canvas.height = 0;
    return cropped ? new File([cropped], name.replace(/\.[^.]+$/, '.png'), { type: 'image/png' }) : original;
  } catch {
    return original;
  } finally {
    bitmap?.close();
  }
}

function assertArtworkPrintReady(objects, sources, message) {
  for (const object of objects) {
    if (object.objects) assertArtworkPrintReady(object.objects, sources, message);
    if ((object.mangoPrintState && object.mangoPrintState !== 'ready') || sources.get(object.sourceImageId)?.preview) {
      throw new Error(message);
    }
  }
}

function replaceArtworkPixels(image, element) {
  const width = element.naturalWidth || element.width;
  const height = element.naturalHeight || element.height;
  if (!width || !height) throw new Error('The original image could not be read.');
  const scaleX = image.scaleX * image.width / width;
  const scaleY = image.scaleY * image.height / height;
  image.setElement(element, { width, height });
  image.set({ scaleX, scaleY });
  image.setCoords();
  if (image.group) image.group.dirty = true;
}

function decodeArtworkFile(file, signal) {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(new DOMException('Aborted', 'AbortError')); return; }
    const url = URL.createObjectURL(file);
    const image = new Image();
    const finish = (error) => {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      image.onload = image.onerror = null;
      URL.revokeObjectURL(url);
      if (error) reject(error); else resolve(image);
    };
    const abort = () => finish(new DOMException('Aborted', 'AbortError'));
    const timer = setTimeout(() => finish(new Error('The original image could not be read.')), 30000);
    signal.addEventListener('abort', abort, { once: true });
    image.onload = () => finish();
    image.onerror = () => finish(new Error('The original image could not be read.'));
    image.src = url;
  });
}

function createArtworkUpgradeManager({ signal, isCurrent, load, prepare, decode, apply, changed }) {
  const tasks = new Map();
  const start = async (image, request) => {
    tasks.get(image)?.controller.abort();
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener('abort', abort, { once: true });
    const task = { image, request, controller, state: 'loading', progress: '' };
    tasks.set(image, task);
    image.mangoPrintState = 'loading';
    changed(tasks);
    const current = () => !signal.aborted && !controller.signal.aborted && tasks.get(image) === task && isCurrent(image);
    try {
      if (!current()) return;
      const blob = await load(request, controller.signal, (progress) => {
        if (!current()) return;
        task.progress = progress;
        changed(tasks);
      });
      if (!current()) return;
      const file = await prepare(blob, request);
      const element = await decode(file, controller.signal);
      if (!current()) return;
      await apply(image, element, file, controller.signal);
      if (!current()) return;
      image.mangoPrintState = 'ready';
      tasks.delete(image);
    } catch (error) {
      if (!current()) return;
      image.mangoPrintState = 'error';
      task.state = 'error';
      task.error = error.message;
    } finally {
      signal.removeEventListener('abort', abort);
      if (tasks.get(image) === task && (!isCurrent(image) || signal.aborted || controller.signal.aborted)) tasks.delete(image);
      if (!signal.aborted) changed(tasks);
    }
  };
  return {
    start,
    remove(image) { tasks.get(image)?.controller.abort(); tasks.delete(image); if (!signal.aborted) changed(tasks); },
    retry() { for (const task of [...tasks.values()]) if (task.state === 'error') void start(task.image, task.request); }
  };
}

function getArtworkDelivery(entries, id, sourceUrl, pageUrl) {
  const entry = entries?.[id];
  if (!entry || entry.source !== sourceUrl) return null;
  try {
    const url = new URL(entry.url, pageUrl);
    const page = new URL(pageUrl);
    if (url.protocol !== 'https:' || (url.origin !== page.origin && url.hostname !== 'cdn.shopify.com')) return null;
    return url.href;
  } catch { return null; }
}

async function fetchArtworkPrint(url, { signal, onProgress = () => {}, timeoutMs = 240000 } = {}) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener('abort', abort, { once: true });
  let expired = false;
  const timer = setTimeout(() => { expired = true; controller.abort(); }, timeoutMs);
  let reader;
  let idleTimer;
  const resetIdleTimer = () => { clearTimeout(idleTimer); idleTimer = setTimeout(() => { expired = true; controller.abort(); }, 30000); };
  resetIdleTimer();
  try {
    const response = await fetch(url, { credentials: 'omit', signal: controller.signal, cache: 'default', headers: { Accept: 'image/png, image/jpeg' } });
    resetIdleTimer();
    if (!response.ok) throw new Error('This design is unavailable. Please try again.');
    const type = (response.headers.get('Content-Type') || '').split(';')[0].trim().toLowerCase();
    if (!/^image\/(png|jpeg)$/.test(type)) throw new Error('This design could not be read. Please try another one.');
    const total = Number(response.headers.get('Content-Length')) || 0;
    if (total > MAX_SINGLE_IMAGE_BYTES) throw new Error('This design exceeds the image size limit.');
    let blob;
    if (response.body?.getReader) {
      reader = response.body.getReader();
      const chunks = [];
      let received = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        resetIdleTimer();
        received += value.byteLength;
        if (received > MAX_SINGLE_IMAGE_BYTES) throw new Error('This design exceeds the image size limit.');
        chunks.push(value);
        onProgress(received, total);
      }
      blob = new Blob(chunks, { type });
    } else {
      blob = await response.blob();
    }
    if (!blob.size || blob.size > MAX_SINGLE_IMAGE_BYTES) throw new Error('The downloaded image is incomplete. Please try again.');
    onProgress(blob.size, blob.size);
    return blob;
  } catch (error) {
    if (reader) await reader.cancel().catch(() => {});
    if (expired) throw new Error('The image took too long to load. Please select it again to retry.');
    throw error;
  } finally {
    clearTimeout(timer);
    clearTimeout(idleTimer);
    signal?.removeEventListener('abort', abort);
    reader?.releaseLock();
  }
}

function createArtworkPrintLoader(entries, pageUrl, editorSignal) {
  const memory = new Map();
  let bytes = 0;
  return async (id, sourceUrl, onProgress, onDelivery = () => {}, requestSignal = editorSignal) => {
    const lifecycleSignal = editorSignal;
    const linked = new AbortController();
    const abort = () => linked.abort();
    lifecycleSignal.addEventListener('abort', abort, { once: true });
    requestSignal.addEventListener('abort', abort, { once: true });
    if (lifecycleSignal.aborted || requestSignal.aborted) abort();
    const signal = linked.signal;
    try {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const fastUrl = getArtworkDelivery(entries, id, sourceUrl, pageUrl);
      // Only immutable, content-addressed CDN copies are retained in memory.
      if (fastUrl && memory.has(fastUrl)) {
        const blob = memory.get(fastUrl);
        memory.delete(fastUrl); memory.set(fastUrl, blob);
        onDelivery('memory');
        onProgress(blob.size, blob.size);
        return blob;
      }
      let blob;
      if (fastUrl) {
        try { onDelivery('cdn'); blob = await fetchArtworkPrint(fastUrl, { signal, onProgress }); }
        catch (error) { if (signal.aborted) throw error; onDelivery('fallback', error.message); }
      }
      if (!blob) { onDelivery('source'); blob = await fetchArtworkPrint(sourceUrl, { signal, onProgress, timeoutMs: 240000 }); }
      if (fastUrl) {
        bytes -= memory.get(fastUrl)?.size || 0;
        memory.set(fastUrl, blob); bytes += blob.size;
        while (memory.size > 8 || bytes > 32 * 1024 * 1024) {
          const key = memory.keys().next().value;
          bytes -= memory.get(key).size; memory.delete(key);
        }
      }
      return blob;
    } finally {
      lifecycleSignal.removeEventListener('abort', abort);
      requestSignal.removeEventListener('abort', abort);
    }
  };
}

function createArtworkThumbnailCache(signal) {
  const cacheName = 'mangorace-artwork-thumbnails-v1';
  const maxAge = 7 * 24 * 60 * 60 * 1000;
  const maxEntries = 120;
  const maxBytes = 24 * 1024 * 1024;
  const pending = new Map();
  const queue = [];
  let running = 0;
  let writes = Promise.resolve();
  const storage = Promise.resolve().then(() => globalThis.caches?.open(cacheName)).catch(() => null);
  const runNext = () => {
    while (running < 4 && queue.length) {
      const task = queue.shift();
      running += 1;
      task().finally(() => { running -= 1; runNext(); });
    }
  };
  const trim = async (cache) => {
    const keys = await cache.keys();
    const entries = await Promise.all(keys.map(async (key) => {
      const response = await cache.match(key);
      return { key, time: Number(response?.headers.get('X-Mango-Cached-At')), size: Number(response?.headers.get('X-Mango-Bytes')) || 0 };
    }));
    entries.sort((a, b) => a.time - b.time);
    let bytes = entries.reduce((sum, entry) => sum + entry.size, 0);
    let count = entries.length;
    for (const entry of entries) {
      if (!entry.time || Date.now() - entry.time >= maxAge || count > maxEntries || bytes > maxBytes) {
        await cache.delete(entry.key);
        count -= 1;
        bytes -= entry.size;
      }
    }
  };
  const read = async (url, key) => {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const cache = await storage;
    if (cache) {
      try {
        const response = await cache.match(key);
        const age = Date.now() - Number(response?.headers.get('X-Mango-Cached-At'));
        if (response && age >= 0 && age < maxAge) return await response.blob();
      } catch {}
    }
    const response = await fetch(url, { credentials: 'omit', cache: 'no-cache', signal });
    if (!response.ok || !/^image\/(webp|png|jpeg)(?:;|$)/i.test(response.headers.get('Content-Type') || '')) {
      throw new Error('Artwork preview unavailable.');
    }
    const blob = await response.blob();
    if (cache && blob.size <= 2 * 1024 * 1024 && !signal.aborted) {
      writes = writes.then(async () => {
        await cache.put(key, new Response(blob, { headers: {
          'Content-Type': blob.type,
          'X-Mango-Cached-At': String(Date.now()),
          'X-Mango-Bytes': String(blob.size)
        } }));
        await trim(cache);
      }).catch(() => {});
      await writes;
    }
    return blob;
  };
  return {
    load(url, revision) {
      const key = new URL(url);
      key.searchParams.set('_mango_revision', revision);
      const cacheKey = key.href;
      if (pending.has(cacheKey)) return pending.get(cacheKey);
      const result = new Promise((resolve, reject) => {
        queue.push(async () => {
          try { resolve(await read(url, cacheKey)); } catch (error) { reject(error); }
        });
        runNext();
      }).finally(() => pending.delete(cacheKey));
      pending.set(cacheKey, result);
      return result;
    }
  };
}

function openLocalDraftDb() {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB is not available.'));
      return;
    }

    const request = indexedDB.open(LOCAL_DRAFT_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(LOCAL_DRAFT_STORE_NAME)) {
        db.createObjectStore(LOCAL_DRAFT_STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Failed to open local draft storage.'));
  });
}

function withLocalDraftStore(mode, callback) {
  return openLocalDraftDb().then((db) => new Promise((resolve, reject) => {
    const transaction = db.transaction(LOCAL_DRAFT_STORE_NAME, mode);
    const store = transaction.objectStore(LOCAL_DRAFT_STORE_NAME);
    let request;

    try {
      request = callback(store);
    } catch (error) {
      db.close();
      reject(error);
      return;
    }

    transaction.oncomplete = () => {
      db.close();
      resolve(request?.result);
    };
    transaction.onerror = () => {
      db.close();
      reject(transaction.error || new Error('Local draft storage failed.'));
    };
  }));
}

function saveLocalDraftRecord(record) {
  return withLocalDraftStore('readwrite', (store) => store.put(record));
}

function readLocalDraftRecord(key) {
  return withLocalDraftStore('readonly', (store) => store.get(key));
}

function deleteLocalDraftRecord(key) {
  return withLocalDraftStore('readwrite', (store) => store.delete(key)).catch(() => {});
}

function parseNumber(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function getCustomerVisibleTemplateId(templateId) {
  return templateId === 'bottle-cylinder-750ml' ? 'bottle-cylinder-850ml' : templateId;
}

function getTemplateConfig(root) {
  return {
    productType: root.dataset.mangoProductType || 'bottle',
    templateId: root.dataset.mangoTemplateId || 'bottle-cylinder-750ml',
    templateName: root.dataset.mangoTemplateName || '850ml cylindrical bottle',
    sku: root.dataset.mangoSku || 'MR-BOTTLE-CUSTOM-DRAFT',
    textureWidth: parseNumber(root.dataset.mangoCanvasWidth, DEFAULT_TEXTURE_WIDTH),
    textureHeight: parseNumber(root.dataset.mangoCanvasHeight, DEFAULT_TEXTURE_HEIGHT),
    physicalWidthMm: parseNumber(root.dataset.mangoPhysicalWidthMm, DEFAULT_PHYSICAL_WIDTH_MM),
    physicalHeightMm: parseNumber(root.dataset.mangoPhysicalHeightMm, DEFAULT_PHYSICAL_HEIGHT_MM),
    dpi: parseNumber(root.dataset.mangoDpi, 900),
    maxImages: parseNumber(root.dataset.mangoMaxImages, MAX_SOURCE_IMAGES),
    maxSingleImageBytes: parseNumber(root.dataset.mangoMaxSingleImageMb, 30) * 1024 * 1024,
    maxTotalImageBytes: parseNumber(root.dataset.mangoMaxTotalImageMb, 100) * 1024 * 1024
  };
}

function readTemplateCard(card) {
  if (!card) return null;
  return {
    templateId: card.dataset.templateId || '',
    productType: card.dataset.productType || 'bottle',
    templateName: card.dataset.templateName || card.textContent.trim(),
    sku: card.dataset.sku || 'MR-BOTTLE-CUSTOM-DRAFT',
    modelUrl: card.dataset.modelUrl || '',
    variantId: card.dataset.variantId || '',
    bodyMeshName: card.dataset.bodyMeshName || 'Bottle_Body',
    customAreaName: card.dataset.customAreaName || 'Custom_Area',
    capMeshName: card.dataset.capMeshName || 'Bottle_Cap',
    mouthpieceMeshName: card.dataset.mouthpieceMeshName || 'Bottle_Mouthpiece',
    customUvChannel: card.dataset.customUvChannel || '1',
    modelRotation: card.dataset.modelRotation || '0,0,90',
    textureRotation: card.dataset.textureRotation || '0',
    frameDistance: card.dataset.frameDistance || '1.2',
    frameTarget: card.dataset.frameTarget || 'model',
    cameraView: card.dataset.cameraView || 'front-z',
    canvasWidth: card.dataset.canvasWidth || DEFAULT_TEXTURE_WIDTH,
    canvasHeight: card.dataset.canvasHeight || DEFAULT_TEXTURE_HEIGHT,
    physicalWidthMm: card.dataset.physicalWidthMm || DEFAULT_PHYSICAL_WIDTH_MM,
    physicalHeightMm: card.dataset.physicalHeightMm || DEFAULT_PHYSICAL_HEIGHT_MM,
    dpi: card.dataset.dpi || 900,
    maxImages: card.dataset.maxImages || MAX_SOURCE_IMAGES,
    maxSingleImageMb: card.dataset.maxSingleImageMb || 30,
    maxTotalImageMb: card.dataset.maxTotalImageMb || 100,
    guideMode: card.dataset.guideMode || 'center'
  };
}

function applyTemplate(root, viewerRoot, template) {
  if (!template) return;
  root.dataset.mangoTemplateId = template.templateId;
  root.dataset.mangoProductType = template.productType;
  root.dataset.mangoTemplateName = template.templateName;
  root.dataset.mangoSku = template.sku;
  root.dataset.mangoCanvasWidth = template.canvasWidth;
  root.dataset.mangoCanvasHeight = template.canvasHeight;
  root.dataset.mangoPhysicalWidthMm = template.physicalWidthMm;
  root.dataset.mangoPhysicalHeightMm = template.physicalHeightMm;
  root.dataset.mangoDpi = template.dpi;
  root.dataset.mangoMaxImages = template.maxImages;
  root.dataset.mangoMaxSingleImageMb = template.maxSingleImageMb;
  root.dataset.mangoMaxTotalImageMb = template.maxTotalImageMb;
  if (template.variantId) root.dataset.mangoVariantId = template.variantId;
  if (viewerRoot) {
    viewerRoot.dataset.modelUrl = template.modelUrl;
    viewerRoot.dataset.bodyMeshName = template.bodyMeshName;
    viewerRoot.dataset.customAreaName = template.customAreaName;
    viewerRoot.dataset.capMeshName = template.capMeshName;
    viewerRoot.dataset.mouthpieceMeshName = template.mouthpieceMeshName;
    viewerRoot.dataset.customUvChannel = template.customUvChannel;
    viewerRoot.dataset.modelRotation = template.modelRotation;
    viewerRoot.dataset.textureRotation = template.textureRotation;
    viewerRoot.dataset.frameDistance = template.frameDistance;
    viewerRoot.dataset.frameTarget = template.frameTarget;
    viewerRoot.dataset.cameraView = template.cameraView;
  }
  const guideSelect = root.querySelector('[data-mango-guide-mode]');
  if (guideSelect) guideSelect.value = template.guideMode;
  const nameTarget = root.querySelector('[data-mango-current-template-name]');
  if (nameTarget) nameTarget.textContent = template.templateName;
  const sizeTarget = root.querySelector('[data-mango-current-template-size]');
  if (sizeTarget) {
    sizeTarget.textContent = `Canvas size: ${template.canvasWidth} x ${template.canvasHeight} px; physical size: ${template.physicalWidthMm} x ${template.physicalHeightMm} mm`;
  }
}

function initTemplatePicker(section) {
  if (initializedTemplatePickers.has(section)) return;
  const root = section.querySelector('[data-mango-customizer]');
  const viewerRoot = section.querySelector('[data-mango-3d-viewer]');
  const cards = [...section.querySelectorAll('[data-mango-template-card]')];
  const select = section.querySelector('[data-mango-template-select]');
  const options = [...(select?.querySelectorAll('[data-mango-template-option]') || [])];
  const templates = options.length ? options : cards;
  if (!root || !templates.length) return;
  initializedTemplatePickers.add(section);

  const selectedTemplate = select?.selectedOptions?.[0]
    || cards.find((card) => card.getAttribute('aria-pressed') === 'true')
    || templates[0];
  applyTemplate(root, viewerRoot, readTemplateCard(selectedTemplate));
  setWorkflowStep(section, 1);

  select?.addEventListener('change', () => {
    const template = readTemplateCard(select.selectedOptions[0]);
    applyTemplate(root, viewerRoot, template);
    root.dispatchEvent(new CustomEvent('mango:template-changed', {
      bubbles: true,
      detail: { template }
    }));
    setWorkflowStep(section, 2);
  });

  cards.forEach((card) => {
    card.addEventListener('click', () => {
      if (card.disabled) return;
      cards.forEach((item) => item.setAttribute('aria-pressed', String(item === card)));
      const template = readTemplateCard(card);
      applyTemplate(root, viewerRoot, template);
      root.dispatchEvent(new CustomEvent('mango:template-changed', {
        bubbles: true,
        detail: { template }
      }));
      setWorkflowStep(section, 2);
    });
  });
}

function setWorkflowStep(section, currentStep) {
  const normalizedStep = Math.min(5, Math.max(1, Number(currentStep) || 1));
  section?.querySelectorAll('[data-mango-workflow-step]').forEach((item) => {
    const step = Number(item.dataset.mangoWorkflowStep);
    item.dataset.state = step < normalizedStep ? 'complete' : (step === normalizedStep ? 'current' : 'upcoming');
  });
}

function initCustomizerTour(section) {
  if (customizerTours.has(section)) return;
  const startButton = section.querySelector('[data-mango-tour-start]');
  const customizer = section.querySelector('[data-mango-customizer]');
  if (!startButton || !customizer) return;
  const titleId = `${section.id || 'mango-customizer'}-guided-tour-title`.replace(/[^a-zA-Z0-9_-]/g, '-');

  const steps = [
    {
      target: '[data-mango-tour-target="setup"]',
      title: 'Choose your setup',
      text: 'Select the bottle style, guide layout, and matte or gloss finish. Only production-ready bottle styles appear here.'
    },
    {
      target: '[data-mango-tour-target="upload"]',
      title: 'Upload your artwork',
      text: 'Choose one or more PNG or JPG files. Your original files are uploaded securely when you confirm the design.'
    },
    {
      target: '[data-mango-tour-target="canvas"]',
      title: 'Arrange and preview',
      text: 'Drag artwork to position it, use the handles or two fingers to resize it, and rotate the 3D bottle to inspect the result.'
    },
    {
      target: '[data-mango-tour-target="confirm"]',
      title: 'Confirm the design',
      text: 'Accept the artwork authorization, choose the quantity, then confirm. Sign in when prompted so the design is saved to your account.'
    },
    {
      target: '[data-mango-tour-target="checkout"]',
      title: 'Add to cart and pay',
      text: 'After confirmation, add the design to your cart. In the cart, select Checkout, enter delivery details, and complete payment.'
    }
  ];

  const overlay = document.createElement('div');
  overlay.className = 'mango-guided-tour';
  overlay.hidden = true;
  overlay.innerHTML = `
    <div class="mango-guided-tour__spotlight" data-mango-tour-spotlight aria-hidden="true"></div>
    <div class="mango-guided-tour__panel" role="dialog" aria-labelledby="${titleId}">
      <div class="mango-guided-tour__progress" data-mango-tour-progress></div>
      <p class="mango-guided-tour__eyebrow" data-mango-tour-count></p>
      <h2 id="${titleId}" data-mango-tour-title></h2>
      <p data-mango-tour-text></p>
      <div class="mango-guided-tour__actions">
        <button type="button" data-mango-tour-skip>Skip tour</button>
        <div>
          <button type="button" data-mango-tour-back>Back</button>
          <button type="button" data-mango-tour-next>Next</button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const spotlight = overlay.querySelector('[data-mango-tour-spotlight]');
  const panel = overlay.querySelector('.mango-guided-tour__panel');
  const progress = overlay.querySelector('[data-mango-tour-progress]');
  const count = overlay.querySelector('[data-mango-tour-count]');
  const title = overlay.querySelector('[data-mango-tour-title]');
  const text = overlay.querySelector('[data-mango-tour-text]');
  const skipButton = overlay.querySelector('[data-mango-tour-skip]');
  const backButton = overlay.querySelector('[data-mango-tour-back]');
  const nextButton = overlay.querySelector('[data-mango-tour-next]');
  let active = false;
  let currentIndex = 0;
  let previousFocus = null;

  const storeCompletion = () => {
    try {
      window.localStorage.setItem(CUSTOMIZER_TOUR_STORAGE_KEY, 'complete');
    } catch (error) {
      // The tour still works when private browsing blocks persistent storage.
    }
  };

  const positionTour = () => {
    if (!active) return;
    const target = section.querySelector(steps[currentIndex].target);
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const padding = 8;
    spotlight.style.top = `${Math.max(6, rect.top - padding)}px`;
    spotlight.style.left = `${Math.max(6, rect.left - padding)}px`;
    spotlight.style.width = `${Math.min(window.innerWidth - 12, rect.width + (padding * 2))}px`;
    spotlight.style.height = `${Math.min(window.innerHeight - 12, rect.height + (padding * 2))}px`;

    panel.style.removeProperty('bottom');
    panel.style.width = `${Math.min(380, window.innerWidth - 24)}px`;
    const panelRect = panel.getBoundingClientRect();
    if (window.innerWidth < 640) {
      panel.style.left = '12px';
      panel.style.top = 'auto';
      panel.style.bottom = '12px';
      return;
    }
    const preferredTop = rect.bottom + 16;
    const top = preferredTop + panelRect.height <= window.innerHeight - 16
      ? preferredTop
      : Math.max(16, rect.top - panelRect.height - 16);
    const left = Math.min(window.innerWidth - panelRect.width - 16, Math.max(16, rect.left));
    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
  };

  const renderStep = () => {
    const step = steps[currentIndex];
    const target = section.querySelector(step.target);
    count.textContent = `GUIDED TOUR ${currentIndex + 1} OF ${steps.length}`;
    title.textContent = step.title;
    text.textContent = step.text;
    progress.style.setProperty('--mango-tour-progress', `${((currentIndex + 1) / steps.length) * 100}%`);
    backButton.hidden = currentIndex === 0;
    nextButton.textContent = currentIndex === steps.length - 1 ? 'Finish' : 'Next';
    target?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    window.requestAnimationFrame(positionTour);
    window.setTimeout(positionTour, 360);
  };

  const finishTour = () => {
    if (!active) return;
    active = false;
    overlay.hidden = true;
    document.body.classList.remove('mango-guided-tour-open');
    window.removeEventListener('resize', positionTour);
    window.removeEventListener('scroll', positionTour, true);
    document.removeEventListener('keydown', onKeydown);
    storeCompletion();
    previousFocus?.focus?.();
  };

  const onKeydown = (event) => {
    if (!active) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      finishTour();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...panel.querySelectorAll('button:not([hidden])')];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const startTour = () => {
    previousFocus = document.activeElement;
    currentIndex = 0;
    active = true;
    overlay.hidden = false;
    document.body.classList.add('mango-guided-tour-open');
    window.addEventListener('resize', positionTour);
    window.addEventListener('scroll', positionTour, true);
    document.addEventListener('keydown', onKeydown);
    renderStep();
    nextButton.focus();
  };

  startButton.addEventListener('click', startTour);
  skipButton.addEventListener('click', finishTour);
  backButton.addEventListener('click', () => {
    currentIndex = Math.max(0, currentIndex - 1);
    renderStep();
  });
  nextButton.addEventListener('click', () => {
    if (currentIndex >= steps.length - 1) {
      finishTour();
      return;
    }
    currentIndex += 1;
    renderStep();
  });

  let tourComplete = false;
  try {
    tourComplete = window.localStorage.getItem(CUSTOMIZER_TOUR_STORAGE_KEY) === 'complete';
  } catch (error) {
    tourComplete = false;
  }
  const isResumingDraft = new URLSearchParams(window.location.search).has('resume_design');
  const autoStartTimer = section.dataset.mangoTourAutostart !== 'false' && !tourComplete && !isResumingDraft && !window.Shopify?.designMode
    ? window.setTimeout(startTour, 900)
    : 0;

  customizerTours.set(section, {
    destroy() {
      active = false;
      if (autoStartTimer) window.clearTimeout(autoStartTimer);
      overlay.remove();
      document.body.classList.remove('mango-guided-tour-open');
      window.removeEventListener('resize', positionTour);
      window.removeEventListener('scroll', positionTour, true);
      document.removeEventListener('keydown', onKeydown);
      customizerTours.delete(section);
    }
  });
}

function findMeshByName(object, names) {
  const wanted = names.filter(Boolean);
  let found = null;
  const matchesName = (item) => item && wanted.includes(item.name);
  object.traverse((child) => {
    if (found || !child.isMesh) return;

    let current = child;
    while (current) {
      if (matchesName(current)) {
        found = child;
        return;
      }
      current = current.parent;
    }

    if (matchesName(child.geometry)) {
      found = child;
    }
  });
  return found;
}

function applyConfiguredModelTransform(root, model) {
  const rotation = (root.dataset.modelRotation || '')
    .split(',')
    .map((value) => Number.parseFloat(value.trim()));
  if (rotation.length === 3 && rotation.every(Number.isFinite)) {
    model.rotation.set(
      THREE.MathUtils.degToRad(rotation[0]),
      THREE.MathUtils.degToRad(rotation[1]),
      THREE.MathUtils.degToRad(rotation[2])
    );
    model.updateMatrixWorld(true);
  }
}

function getPrimaryFrameTarget(root, model) {
  if (root.dataset.frameTarget === 'model') return model;
  return findMeshByName(model, [
    root.dataset.bodyMeshName || 'Bottle_Body',
    root.dataset.customAreaName || 'Custom_Area'
  ]) || model;
}

function frameObject(root, camera, controls, object, frameTarget = object) {
  const originalBox = new THREE.Box3().setFromObject(frameTarget);
  const originalSize = originalBox.getSize(new THREE.Vector3());
  const originalMaxSize = Math.max(originalSize.x, originalSize.y, originalSize.z) || 3;
  const scaleFactor = 1;
  const box = new THREE.Box3().setFromObject(frameTarget);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const maxSize = Math.max(size.x, size.y, size.z) || originalMaxSize;
  const sphere = box.getBoundingSphere(new THREE.Sphere());
  const verticalFov = THREE.MathUtils.degToRad(camera.fov);
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * Math.max(camera.aspect, 0.1));
  const limitingFov = Math.max(0.1, Math.min(verticalFov, horizontalFov));
  const configuredDistance = Number.parseFloat(root.dataset.frameDistance || '');
  const distanceFactor = Number.isFinite(configuredDistance) ? configuredDistance : 1.2;
  const distance = (sphere.radius / Math.sin(limitingFov / 2)) * distanceFactor;

  object.position.sub(center);
  if (root.dataset.cameraView === 'side-y') {
    camera.position.set(distance * 0.25, distance, distance * 0.18);
  } else {
    camera.position.set(distance * 0.25, distance * 0.18, distance);
  }
  camera.near = 0.001;
  camera.far = Math.max(50, distance * 20);
  camera.updateProjectionMatrix();

  controls.minDistance = Math.max(0.01, distance * 0.35);
  controls.maxDistance = Math.max(1, distance * 10);
  controls.target.set(0, 0, 0);
  controls.update();

  return {
    originalSize: [
      originalSize.x.toFixed(3),
      originalSize.y.toFixed(3),
      originalSize.z.toFixed(3)
    ].join(','),
    scaleFactor: scaleFactor.toFixed(5),
    displaySize: [
      size.x.toFixed(3),
      size.y.toFixed(3),
      size.z.toFixed(3)
    ].join(',')
  };
}

function setStatus(root, message) {
  const status = root.querySelector('[data-mango-3d-status]');
  if (!status) return;
  status.textContent = message;
  status.hidden = !message;
}

function applyBottleFinish(material, finish) {
  if (!material) return;
  if (Array.isArray(material)) {
    material.forEach((item) => applyBottleFinish(item, finish));
    return;
  }
  const isGloss = finish === 'gloss';
  const isArtworkOverlay = material.userData?.mangoArtworkOverlay === true;
  material.roughness = isArtworkOverlay
    ? (isGloss ? 0.24 : 0.74)
    : (isGloss ? 0.16 : 0.86);
  material.metalness = isArtworkOverlay ? 0 : (isGloss ? 0.22 : 0.12);
  if ('clearcoat' in material) material.clearcoat = isGloss ? 1 : 0;
  if ('clearcoatRoughness' in material) material.clearcoatRoughness = isGloss ? 0.06 : 1;
  material.needsUpdate = true;
}

function applyCapColor(model, capMeshName, color) {
  if (!model || !color) return;
  const wantedNames = [
    capMeshName,
    'Bottle_Cap',
    'Bottle_Lid',
    'Cap',
    'Lid'
  ].filter(Boolean);
  const capColor = new THREE.Color(color);

  model.traverse((child) => {
    if (!child.isMesh) return;
    let current = child;
    let matched = false;
    while (current) {
      if (wantedNames.includes(current.name)) {
        matched = true;
        break;
      }
      current = current.parent;
    }
    if (!matched) return;

    if (!child.userData.mangoCapMaterialCloned) {
      child.material = Array.isArray(child.material)
        ? child.material.map((material) => material?.clone?.() || material)
        : child.material?.clone?.() || child.material;
      child.userData.mangoCapMaterialCloned = true;
    }

    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      if (!material?.color) return;
      if (material.name === 'Cap_Logo_Recess_White') return;
      material.color.copy(capColor);
      material.needsUpdate = true;
    });
  });
}

function applyMouthpieceColor(model, mouthpieceMeshName, color) {
  if (!model || !color) return;
  const wantedNames = [
    mouthpieceMeshName,
    'Bottle_Mouthpiece',
    'Mouthpiece'
  ].filter(Boolean);
  const mouthpieceColor = new THREE.Color(color);

  model.traverse((child) => {
    if (!child.isMesh) return;
    let current = child;
    let matched = false;
    while (current) {
      if (wantedNames.includes(current.name)) {
        matched = true;
        break;
      }
      current = current.parent;
    }
    if (!matched) return;

    if (!child.userData.mangoMouthpieceMaterialCreated) {
      const sourceMaterials = Array.isArray(child.material) ? child.material : [child.material];
      const mouthpieceMaterials = sourceMaterials.map((material) => new THREE.MeshPhysicalMaterial({
        name: material?.name || 'Mouthpiece_Frosted',
        color: mouthpieceColor,
        roughness: 0.86,
        metalness: 0,
        transmission: 0.28,
        transparent: true,
        opacity: 0.72,
        thickness: 0.018,
        ior: 1.45,
        depthWrite: false
      }));
      child.material = Array.isArray(child.material) ? mouthpieceMaterials : mouthpieceMaterials[0];
      child.userData.mangoMouthpieceMaterialCreated = true;
      child.renderOrder = 3;
    }

    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      if (!material?.color) return;
      material.color.copy(mouthpieceColor);
      material.needsUpdate = true;
    });
  });
}

function initViewer(root) {
  if (viewers.has(root)) return;

  const canvas = root.querySelector('[data-mango-3d-canvas]');
  if (!canvas) return;
  const section = root.closest('.mango-3d-service');
  const customizer = section?.querySelector('[data-mango-customizer]');
  const editor = customizer ? initFabricEditor(customizer) : null;

  const scene = new THREE.Scene();
  const sceneBgColor = root.dataset.bgColor || '#eef2f5';
  scene.background = new THREE.Color(sceneBgColor);

  const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
  const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      preserveDrawingBuffer: true,
      powerPreference: 'default'
    });
  } catch (error) {
    root.dataset.viewerError = 'webgl-init-failed';
    console.error('MangoRace 3D preview could not create a WebGL context.', error);
    setStatus(root, '3D preview is unavailable on this device. You can still complete the flat artwork design.');
    return;
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isCoarsePointer ? 1.5 : 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;

  const configuredLightLevel = Number.parseFloat(root.dataset.lightLevel || '');
  const studioLightLevel = Math.min(1.4, Math.max(0.8, (
    Number.isFinite(configuredLightLevel) ? configuredLightLevel : 115
  ) / 100));
  root.dataset.appliedLightLevel = studioLightLevel.toFixed(2);

  const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0xd6d9dd, 1.05 * studioLightLevel);
  scene.add(hemisphereLight);

  const ambientLight = new THREE.AmbientLight(0xffffff, 0.45 * studioLightLevel);
  scene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xffffff, 2.25 * studioLightLevel);
  directionalLight.position.set(4.5, 5.5, 6);
  scene.add(directionalLight);

  const fillLight = new THREE.DirectionalLight(0xffffff, 1.4 * studioLightLevel);
  fillLight.position.set(-4, 3, 5);
  scene.add(fillLight);

  const frontLight = new THREE.DirectionalLight(0xffffff, 0.9 * studioLightLevel);
  frontLight.position.set(0, 1.5, 6.5);
  scene.add(frontLight);

  const rimLight = new THREE.DirectionalLight(0xffffff, 0.85 * studioLightLevel);
  rimLight.position.set(-3, 4, -4);
  scene.add(rimLight);

  const controls = new OrbitControls(camera, renderer.domElement);
  const reducedMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  controls.enableDamping = !reducedMotionQuery?.matches;
  controls.enablePan = false;
  controls.minDistance = 2.2;
  controls.maxDistance = 8;

  const floor = new THREE.Mesh(
    new THREE.CircleGeometry(2.2, 80),
    new THREE.MeshStandardMaterial({
      color: 0xdfe5ec,
      metalness: 0.05,
      roughness: 0.74
    })
  );
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -1.82;
  scene.add(floor);

  const state = {
    model: null,
    frameId: null,
    texture: null,
    targetMesh: null,
    targetMaterial: null,
    overlayMesh: null,
    textureUpdateListener: null,
    textureUpdateRoot: null,
    isPageVisible: !document.hidden,
    isViewerVisible: true,
    isInteracting: false,
    destroyed: false,
    requestRender: null
  };
  let sampledPixels = false;

  const canRender = () => !state.destroyed && state.isPageVisible && state.isViewerVisible;
  const scheduleRender = () => {
    if (!canRender() || state.frameId) return;
    state.frameId = window.requestAnimationFrame(renderFrame);
  };

  const renderFrame = () => {
    state.frameId = null;
    if (!canRender()) return;

    const controlsChanged = controls.update();
    renderer.render(scene, camera);
    root.dataset.cameraPosition = [
      camera.position.x.toFixed(3),
      camera.position.y.toFixed(3),
      camera.position.z.toFixed(3)
    ].join(',');
    if (!sampledPixels) {
      try {
        const gl = renderer.getContext();
        const sample = new Uint8Array(4);
        gl.readPixels(
          Math.floor(gl.drawingBufferWidth / 2),
          Math.floor(gl.drawingBufferHeight / 2),
          1,
          1,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          sample
        );
        root.dataset.rendered = 'true';
        root.dataset.pixelSample = Array.from(sample).join(',');
      } catch (error) {
        root.dataset.pixelSample = 'unavailable';
      }
      sampledPixels = true;
    }

    if (state.isInteracting || (!reducedMotionQuery?.matches && controlsChanged)) {
      scheduleRender();
    }
  };

  state.requestRender = scheduleRender;

  const clearTextureUpdateListener = () => {
    if (state.textureUpdateListener && state.textureUpdateRoot) {
      state.textureUpdateRoot.removeEventListener('mango:fabric-texture-updated', state.textureUpdateListener);
    }
    state.textureUpdateListener = null;
    state.textureUpdateRoot = null;
  };

  let viewerResizeFrame = null;
  let viewerWidth = 0;
  let viewerHeight = 0;
  const resize = () => {
    const width = Math.round(root.clientWidth);
    const height = Math.round(root.clientHeight || canvas.clientHeight);
    if (!width || !height || (width === viewerWidth && height === viewerHeight)) return;

    viewerWidth = width;
    viewerHeight = height;

    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
    scheduleRender();
  };
  const queueResize = () => {
    if (viewerResizeFrame) window.cancelAnimationFrame(viewerResizeFrame);
    viewerResizeFrame = window.requestAnimationFrame(() => {
      viewerResizeFrame = null;
      resize();
    });
  };

  const showObject = (object) => {
    if (state.model) scene.remove(state.model);
    if (state.overlayMesh) {
      state.overlayMesh.parent?.remove(state.overlayMesh);
      state.overlayMesh = null;
    }
    clearTextureUpdateListener();
    state.texture?.dispose();
    state.texture = null;
    state.model = object;
    scene.add(object);
    applyConfiguredModelTransform(root, object);
    const capOption = customizer?.querySelector('[data-mango-cap-option][aria-pressed="true"]')
      || customizer?.querySelector('[data-mango-cap-option]');
    applyCapColor(object, root.dataset.capMeshName, capOption?.dataset.capColor || '#101010');
    const mouthpieceOption = customizer?.querySelector('[data-mango-mouthpiece-option][aria-pressed="true"]')
      || customizer?.querySelector('[data-mango-mouthpiece-option]');
    applyMouthpieceColor(
      object,
      root.dataset.mouthpieceMeshName,
      mouthpieceOption?.dataset.mouthpieceColor || '#F5F2EC'
    );
    bindEditorTexture(root, object, editor, state);
    const frameInfo = frameObject(
      root,
      camera,
      controls,
      object,
      getPrimaryFrameTarget(root, object)
    );
    root.dataset.modelOriginalSize = frameInfo.originalSize;
    root.dataset.modelScaleFactor = frameInfo.scaleFactor;
    root.dataset.modelDisplaySize = frameInfo.displaySize;
    setStatus(root, '');
    scheduleRender();
  };

  let modelLoadTimeout = null;
  const clearModelLoadTimeout = () => {
    if (modelLoadTimeout) window.clearTimeout(modelLoadTimeout);
    modelLoadTimeout = null;
  };
  const showModelUnavailable = (message, error) => {
    clearModelLoadTimeout();
    root.dataset.modelLoadError = error instanceof Error ? error.message : 'model-load-failed';
    console.error('MangoRace 3D model could not load.', error);
    setStatus(root, message);
  };

  const loadModel = () => {
    clearModelLoadTimeout();
    const modelUrl = (root.dataset.modelUrl || '').trim();
    if (!modelUrl) {
      setStatus(root, 'The 3D bottle model is unavailable. Please refresh the page or try again later.');
      return;
    }

    setStatus(root, 'Loading 3D bottle model...');
    scheduleRender();
    const loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    const decoderPath = (root.dataset.dracoDecoderPath || '').trim();
    if (decoderPath) dracoLoader.setDecoderPath(decoderPath);
    dracoLoader.setDecoderConfig({ type: 'wasm' });
    loader.setDRACOLoader(dracoLoader);
    modelLoadTimeout = window.setTimeout(() => {
      showModelUnavailable(
        'The 3D bottle model is taking longer than usual to load. Please keep this page open while it finishes.',
        new Error('model-load-timeout')
      );
    }, 12000);
    loader.load(
      modelUrl,
      (gltf) => {
        clearModelLoadTimeout();
        dracoLoader.dispose();
        showObject(gltf.scene);
      },
      undefined,
      (error) => {
        dracoLoader.dispose();
        showModelUnavailable(
          'The 3D bottle model is temporarily unavailable. You can still complete the flat artwork design.',
          error
        );
      }
    );
  };

  const onControlsStart = () => {
    state.isInteracting = true;
    scheduleRender();
  };
  const onControlsEnd = () => {
    state.isInteracting = false;
    scheduleRender();
  };
  const onControlsChange = () => scheduleRender();
  const onVisibilityChange = () => {
    state.isPageVisible = !document.hidden;
    scheduleRender();
  };
  const onReducedMotionChange = (event) => {
    controls.enableDamping = !event.matches;
    scheduleRender();
  };
  const onContextLost = (event) => {
    event.preventDefault();
    root.dataset.viewerError = 'webgl-context-lost';
    setStatus(root, '3D preview paused because this device ran out of graphics memory. You can still complete the flat artwork design.');
  };
  const visibilityObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      const entry = entries[0];
      state.isViewerVisible = Boolean(entry?.isIntersecting);
      scheduleRender();
    }, { threshold: 0.01 })
    : null;
  const viewerResizeObserver = 'ResizeObserver' in window
    ? new ResizeObserver(queueResize)
    : null;

  controls.addEventListener('start', onControlsStart);
  controls.addEventListener('end', onControlsEnd);
  controls.addEventListener('change', onControlsChange);
  canvas.addEventListener('webglcontextlost', onContextLost, false);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('resize', queueResize);
  window.addEventListener('orientationchange', queueResize);
  window.visualViewport?.addEventListener?.('resize', queueResize);
  reducedMotionQuery?.addEventListener?.('change', onReducedMotionChange);
  visibilityObserver?.observe(root);
  viewerResizeObserver?.observe(root);
  resize();
  loadModel();

  const onFinishChanged = (event) => {
    applyBottleFinish(state.targetMaterial, event.detail?.finish || 'matte');
    scheduleRender();
  };

  const onCapChanged = (event) => {
    applyCapColor(state.model, root.dataset.capMeshName, event.detail?.color || '#101010');
    scheduleRender();
  };

  const onMouthpieceChanged = (event) => {
    applyMouthpieceColor(
      state.model,
      root.dataset.mouthpieceMeshName,
      event.detail?.color || '#F5F2EC'
    );
    scheduleRender();
  };

  const onTemplateChanged = () => {
    sampledPixels = false;
    loadModel();
  };

  const onSnapshotRequested = () => {
    renderer.render(scene, camera);
    try {
      customizer.dispatchEvent(new CustomEvent('mango:3d-snapshot-ready', {
        detail: { dataUrl: renderer.domElement.toDataURL('image/png') }
      }));
    } catch (error) {
      customizer.dispatchEvent(new CustomEvent('mango:3d-snapshot-ready', {
        detail: { dataUrl: '', error }
      }));
    }
  };

  customizer?.addEventListener('mango:finish-changed', onFinishChanged);
  customizer?.addEventListener('mango:cap-changed', onCapChanged);
  customizer?.addEventListener('mango:mouthpiece-changed', onMouthpieceChanged);
  customizer?.addEventListener('mango:template-changed', onTemplateChanged);
  customizer?.addEventListener('mango:request-3d-snapshot', onSnapshotRequested);

  viewers.set(root, {
    destroy() {
      window.removeEventListener('resize', queueResize);
      window.removeEventListener('orientationchange', queueResize);
      window.visualViewport?.removeEventListener?.('resize', queueResize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      reducedMotionQuery?.removeEventListener?.('change', onReducedMotionChange);
      visibilityObserver?.disconnect();
      viewerResizeObserver?.disconnect();
      controls.removeEventListener('start', onControlsStart);
      controls.removeEventListener('end', onControlsEnd);
      controls.removeEventListener('change', onControlsChange);
      canvas.removeEventListener('webglcontextlost', onContextLost, false);
      customizer?.removeEventListener('mango:finish-changed', onFinishChanged);
      customizer?.removeEventListener('mango:cap-changed', onCapChanged);
      customizer?.removeEventListener('mango:mouthpiece-changed', onMouthpieceChanged);
      customizer?.removeEventListener('mango:template-changed', onTemplateChanged);
      customizer?.removeEventListener('mango:request-3d-snapshot', onSnapshotRequested);
      state.destroyed = true;
      window.cancelAnimationFrame(state.frameId);
      window.cancelAnimationFrame(viewerResizeFrame);
      clearModelLoadTimeout();
      clearTextureUpdateListener();
      controls.dispose();
      state.texture?.dispose();
      renderer.dispose();
    }
  });
}

function createCustomizationUploadClient({ request, apiBase, text, onStage, makeXhr = () => new XMLHttpRequest(), now = () => Date.now(), delay = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms)) }) {
  const errorFor = (code, key, status = 0) => Object.assign(new Error(text(key)), { code, status });
  const transient = (error) => error.name === 'AbortError' || error.code === 'NETWORK'
    || error.code === 'IDLE_TIMEOUT' || error.code === 'UPLOAD_TIMEOUT'
    || error.status === 408 || error.status === 429 || error.status >= 500;
  const jobUrl = (jobId) => `${apiBase()}/customizations/customer/uploads/${encodeURIComponent(jobId)}`;
  const jsonRequest = async (url, options, timeout) => {
    let response;
    try { response = await request(url, options, timeout); }
    catch (error) {
      if (error.name === 'AbortError') throw error;
      throw errorFor('NETWORK', 'connection_paused');
    }
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const key = response.status === 401 || response.status === 403 ? 'sign_in_again'
        : response.status === 409 || response.status === 410 ? 'session_changed'
          : response.status === 400 ? 'object_conflict' : 'connection_paused';
      throw errorFor('HTTP', key, response.status);
    }
    if (!data || typeof data !== 'object') throw errorFor('NETWORK', 'connection_paused');
    return data;
  };
  const readWithRetry = async (url, timeout = 20000) => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try { return await jsonRequest(url, { method: 'GET', headers: { Accept: 'application/json' }, cache: 'no-store' }, timeout); }
      catch (error) {
        if (!transient(error) || attempt === 2) throw error;
        onStage({ stage: 'reconnecting' });
        await delay(1000 * (attempt + 1));
      }
    }
  };
  const readJob = (jobId) => readWithRetry(jobUrl(jobId));
  const probeObject = async (session, task) => {
    onStage({ stage: 'checking_file' });
    const data = await readWithRetry(`${jobUrl(session.jobId)}/objects/${encodeURIComponent(task.upload.objectId)}`, 60000);
    if (typeof data.uploaded !== 'boolean') throw errorFor('NETWORK', 'connection_paused');
    return data.uploaded;
  };
  const endpointFor = (upload, fallback) => {
    const primary = new URL(upload.uploadUrl);
    const match = primary.hostname.match(/^([a-z0-9][a-z0-9-]+)\.(oss-[a-z0-9-]+)\.aliyuncs\.com$/i);
    if (!match || match[2].endsWith('-internal') || primary.protocol !== 'https:' || primary.username || primary.password
      || primary.port || primary.pathname !== '/' || primary.search || primary.hash) throw errorFor('INVALID', 'invalid_authorization');
    if (!fallback || upload.uploadMode !== 'accelerated' || !upload.fallbackUploadUrl) return primary.toString();
    const alternative = new URL(upload.fallbackUploadUrl);
    if (alternative.protocol !== 'https:' || alternative.username || alternative.password || alternative.port
      || alternative.pathname !== '/' || alternative.search || alternative.hash
      || !alternative.hostname.startsWith(`${match[1]}.oss-`)
      || !/\.oss-(?!accelerate|.*-internal)[a-z0-9-]+\.aliyuncs\.com$/i.test(alternative.hostname)) {
      throw errorFor('INVALID', 'invalid_authorization');
    }
    return alternative.toString();
  };
  const assertPolicy = (session, task) => {
    const expiry = Date.parse(task.upload?.expiresAt || session.expiresAt || '');
    if (!Number.isFinite(expiry) || expiry <= now()) throw errorFor('EXPIRED', 'session_expired');
    if (!task.upload?.objectId || !task.upload.fields?.key || task.upload.fields['x-oss-forbid-overwrite'] !== 'true') {
      throw errorFor('INVALID', 'invalid_authorization');
    }
  };
  const sendObject = (session, task, batch, fallback, report) => new Promise((resolve, reject) => {
    let xhr;
    let idleTimer;
    let absoluteTimer;
    let settled = false;
    let lastUploaded = 0;
    const finish = (error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(idleTimer);
      window.clearTimeout(absoluteTimer);
      if (xhr) batch.active.delete(xhr);
      if (error) reject(error); else resolve();
    };
    const expire = (code, key) => {
      finish(errorFor(code, key));
      xhr?.abort();
    };
    const resetIdle = () => {
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => expire('IDLE_TIMEOUT', 'upload_stalled'), 60000);
    };
    try {
      assertPolicy(session, task);
      if (batch.stopped) throw errorFor('CANCELED', 'upload_paused');
      const form = new FormData();
      Object.entries(task.upload.fields).forEach(([key, value]) => form.append(key, String(value)));
      form.append('file', task.blob, task.fileName);
      xhr = makeXhr();
      xhr.open('POST', endpointFor(task.upload, fallback), true);
      xhr.upload.onprogress = (event) => {
        if (settled) return;
        if (event.loaded > lastUploaded) { lastUploaded = event.loaded; resetIdle(); }
        const sent = event.lengthComputable && event.total > 0 ? task.blob.size * event.loaded / event.total : event.loaded;
        task.loaded = Math.min(Math.max(0, task.blob.size - 1), Math.max(0, Math.round(sent || 0)));
        report();
      };
      xhr.onload = () => finish(xhr.status >= 200 && xhr.status < 300 ? null
        : errorFor('UPLOAD_HTTP', xhr.status === 401 || xhr.status === 403 ? 'session_expired' : 'upload_failed', xhr.status));
      xhr.onerror = () => finish(errorFor('NETWORK', 'connection_paused'));
      xhr.ontimeout = () => finish(errorFor('UPLOAD_TIMEOUT', 'upload_slow'));
      xhr.onabort = () => finish(errorFor('CANCELED', 'upload_paused'));
      batch.active.add(xhr);
      task.needsVerification = true;
      resetIdle();
      absoluteTimer = window.setTimeout(() => expire('UPLOAD_TIMEOUT', 'upload_slow'), 180000);
      xhr.send(form);
    } catch (error) { finish(error); }
  });
  const uploadTasks = async (session) => {
    const batch = { active: new Set(), stopped: false };
    const total = session.tasks.reduce((sum, task) => sum + task.blob.size, 0);
    const report = () => onStage({ stage: 'uploading', total,
      loaded: session.tasks.reduce((sum, task) => sum + (task.done ? task.blob.size : task.loaded || 0), 0) });
    let next = 0;
    let failure;
    const finishTask = (task) => { task.done = true; task.needsVerification = false; task.loaded = task.blob.size; report(); };
    const worker = async () => {
      while (!batch.stopped && next < session.tasks.length) {
        const task = session.tasks[next++];
        if (task.done) continue;
        try {
          assertPolicy(session, task);
          if (task.needsVerification && await probeObject(session, task)) { finishTask(task); continue; }
          for (let attempt = 1; attempt <= 3; attempt += 1) {
            if (batch.stopped) return;
            task.loaded = 0;
            report();
            try {
              await sendObject(session, task, batch, attempt > 1, report);
              finishTask(task);
              break;
            } catch (error) {
              if (batch.stopped || error.code === 'CANCELED') throw error;
              if (!transient(error) && error.status !== 409) throw error;
              if (await probeObject(session, task)) { finishTask(task); break; }
              if (error.status === 409) throw errorFor('CONFLICT', 'object_conflict', 409);
              if (attempt === 3) throw error;
              assertPolicy(session, task);
              onStage({ stage: 'retrying', attempt: attempt + 1, limit: 3 });
              await delay(attempt === 1 ? 1000 : 2500);
            }
          }
        } catch (error) {
          if (!failure) failure = error;
          batch.stopped = true;
          for (const xhr of [...batch.active]) xhr.abort();
        }
      }
    };
    report();
    await Promise.allSettled(Array.from({ length: Math.min(3, session.tasks.length) }, () => worker()));
    if (failure) throw failure;
  };
  const waitForJob = async (session, initial) => {
    const deadline = now() + 12 * 60 * 1000;
    let job = initial;
    while (now() < deadline) {
      job = job || await readJob(session.jobId);
      session.phase = job.status;
      if (job.status === 'SUCCEEDED' && job.result) { session.result = job.result; return job.result; }
      if (['FAILED', 'EXPIRED', 'CANCELED'].includes(job.status)) throw errorFor('TERMINAL', job.status === 'FAILED' ? 'processing_failed' : 'session_expired');
      if (!['QUEUED', 'PROCESSING'].includes(job.status)) throw errorFor('JOB_STATE', 'processing_uncertain');
      onStage({ stage: job.status === 'QUEUED' ? 'queued' : 'processing', progress: Number(job.progress) || 0,
        position: Number(job.queuePosition) || 0 });
      await delay(3000);
      job = null;
    }
    throw errorFor('PROCESSING_TIMEOUT', 'processing_background');
  };
  const completeJob = async (session) => {
    session.phase = 'VERIFYING';
    onStage({ stage: 'verifying' });
    let job;
    try {
      job = await jsonRequest(`${jobUrl(session.jobId)}/complete`, {
        method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: '{}'
      }, 120000);
    } catch (error) {
      if (!transient(error) && error.status !== 409) throw error;
      job = await readJob(session.jobId);
      if (job.status === 'CREATED') throw errorFor('COMPLETE_UNCERTAIN', 'verification_paused');
    }
    return await waitForJob(session, job);
  };
  const resume = async (session, { inspect = true } = {}) => {
    if (session.result) {
      if (Date.parse(session.result.expiresAt || '') > now()) return session.result;
      session.result = null;
      throw errorFor('TERMINAL', 'session_expired');
    }
    if (inspect) {
      const job = await readJob(session.jobId);
      if (job.status !== 'CREATED') return await waitForJob(session, job);
      session.phase = 'CREATED';
    }
    try { await uploadTasks(session); }
    catch (error) {
      if (error.status === 409) {
        const job = await readJob(session.jobId);
        if (['QUEUED', 'PROCESSING', 'SUCCEEDED'].includes(job.status)) return await waitForJob(session, job);
      }
      throw error;
    }
    return await completeJob(session);
  };
  const cancelForChangedDesign = async (session) => {
    const current = await readJob(session.jobId);
    if (['QUEUED', 'PROCESSING', 'SUCCEEDED'].includes(current.status)) return { result: await waitForJob(session, current) };
    if (['CANCELED', 'FAILED', 'EXPIRED'].includes(current.status)) return { canceled: true };
    if (current.status !== 'CREATED') throw errorFor('JOB_STATE', 'processing_uncertain');
    try {
      const data = await jsonRequest(jobUrl(session.jobId), { method: 'DELETE', headers: { Accept: 'application/json' } }, 10000);
      if (data.ok !== true) throw errorFor('JOB_STATE', 'processing_uncertain');
      return { canceled: true };
    } catch (error) {
      const latest = await readJob(session.jobId);
      if (['QUEUED', 'PROCESSING', 'SUCCEEDED'].includes(latest.status)) return { result: await waitForJob(session, latest) };
      if (['CANCELED', 'FAILED', 'EXPIRED'].includes(latest.status)) return { canceled: true };
      throw error;
    }
  };
  return { resume, readJob, uploadTasks, completeJob, cancelForChangedDesign };
}

function initFabricEditor(root) {
  if (editorBySection.has(root)) return editorBySection.get(root);

  const section = root.closest('.mango-3d-service');
  const canvasEl = root.querySelector('[data-mango-fabric-canvas]');
  const uploadInput = root.querySelector('[data-mango-logo-upload]');
  const libraryRoot = root.querySelector('[data-mango-artwork-library]');
  const libraryGrid = libraryRoot?.querySelector('[data-mango-library-grid]');
  const libraryStatus = libraryRoot?.querySelector('[data-mango-library-status]');
  const libraryFilters = libraryRoot?.querySelector('[data-mango-library-filters]');
  const librarySearch = libraryRoot?.querySelector('[data-mango-library-search]');
  const libraryCategories = libraryRoot?.querySelector('[data-mango-library-categories]');
  const libraryMore = libraryRoot?.querySelector('[data-mango-library-more]');
  const libraryDialog = libraryRoot?.querySelector('[data-mango-library-dialog]');
  const libraryOpen = libraryRoot?.querySelector('[data-mango-library-open]');
  const libraryClose = libraryRoot?.querySelector('[data-mango-library-close]');
  const libraryRequest = new AbortController();
  const libraryTransfer = libraryRoot?.querySelector('[data-mango-library-transfer]');
  let libraryDelivery = {};
  try { libraryDelivery = JSON.parse(libraryRoot?.querySelector('[data-mango-artwork-delivery]')?.textContent || '{}'); } catch {}
  const loadLibraryPrint = createArtworkPrintLoader(libraryDelivery, window.location.href, libraryRequest.signal);
  let libraryTransferBusy = false;
  const libraryThumbnails = createArtworkThumbnailCache(libraryRequest.signal);
  const artworkQuality = root.querySelector('[data-mango-artwork-quality]');
  const artworkQualityText = root.querySelector('[data-mango-artwork-quality-text]');
  const artworkRetry = root.querySelector('[data-mango-artwork-retry]');
  const qualityText = (key) => artworkQuality?.dataset[key] || key;
  const artworkPendingMessage = () => qualityText('pendingMessage');
  const personalNameInput = root.querySelector('[data-mango-personal-name]');
  const personalNumberInput = root.querySelector('[data-mango-personal-number]');
  const addPersonalizationButton = root.querySelector('[data-mango-add-personalization]');
  const deleteButton = root.querySelector('[data-mango-delete-selected]');
  const guideModeSelect = root.querySelector('[data-mango-guide-mode]');
  const finishModeSelect = root.querySelector('[data-mango-finish-mode]');
  const capOptions = root.querySelector('[data-mango-cap-options]');
  const mouthpieceOptions = root.querySelector('[data-mango-mouthpiece-options]');
  const ipAgreement = root.querySelector('[data-mango-ip-agreement]');
  const confirmButton = root.querySelector('[data-mango-confirm-design]');
  const addToCartButton = root.querySelector('[data-mango-add-to-cart]');
  const quantityInput = root.querySelector('[data-mango-custom-quantity]');
  const orderStatus = root.querySelector('[data-mango-order-status]');
  const finishField = root.querySelector('[data-mango-finish-field]');
  const designIdField = root.querySelector('[data-mango-design-id-field]');
  const snapshotField = root.querySelector('[data-mango-snapshot-field]');
  const artworkField = root.querySelector('[data-mango-artwork-field]');
  const guideLayer = root.querySelector('[data-mango-guide-layer]');
  const warning = root.querySelector('[data-mango-editor-warning]');
  const uploadLimitStatus = root.querySelector('[data-mango-upload-limit-status]');
  const uploadProgressPanel = root.querySelector('[data-mango-upload-progress-panel]');
  const uploadProgress = root.querySelector('[data-mango-upload-progress]');
  const uploadProgressDetail = root.querySelector('[data-mango-upload-progress-detail]');
  const stage = root.querySelector('[data-mango-customizer-stage]');
  if (!canvasEl) return null;

  let templateConfig = getTemplateConfig(root);
  let textureWidth = templateConfig.textureWidth;
  let textureHeight = templateConfig.textureHeight;
  let maxImages = templateConfig.maxImages;
  let maxSingleImageBytes = templateConfig.maxSingleImageBytes;
  let maxTotalImageBytes = templateConfig.maxTotalImageBytes;

  const fabricCanvas = new fabric.Canvas(canvasEl, {
    width: textureWidth,
    height: textureHeight,
    enableRetinaScaling: false,
    preserveObjectStacking: true,
    selection: true,
    backgroundColor: null
  });
  const hasArtwork = () => fabricCanvas.getObjects().some((object) => object.type !== 'line');
  const showSetupOrArtworkStep = () => setWorkflowStep(section, hasArtwork() ? 3 : 2);
  const textureCanvas = document.createElement('canvas');
  textureCanvas.width = textureWidth;
  textureCanvas.height = textureHeight;
  const textureContext = textureCanvas.getContext('2d');
  let displayScale = 1;
  let textureUpdateFrame = 0;
  let canvasFitFrame = 0;
  let observedStageWidth = 0;
  let stageResizeObserver = null;
  let designConfirmed = false;
  let editingSavedDesign = false;
  let latestSnapshotDataUrl = '';
  let pinchGesture = null;
  let customerUploadToken = '';
  let customerUploadTokenExpiresAt = 0;
  let customerAccountKey = '';
  const sourceImageStore = new Map();
  const savedImageProperties = ['src', 'sourceImageId', 'originalFileName', 'mangoArtworkKind', 'mangoLibraryId', 'mangoPrintState', 'personalizationName', 'personalizationNumber'];
  let uploadSession = null;
  let uploadCreateUncertain = false;
  let submissionInFlight = false;
  let latestUploadStage = 'preparing';
  const uploadDefaults = {
    selected: 'Selected __count__/__max__ images, __bytes__/__limit__',
    preparing: 'Preparing your design. Your original images will stay unchanged.',
    creating: 'Securing an upload slot for your design...',
    uploading: 'Uploading __loaded__ of __total__ (__percent__%). Original quality is preserved.',
    checking_file: 'Checking whether the file arrived safely before trying again...',
    retrying: 'Reconnecting to upload your file (attempt __attempt__ of __limit__)...',
    reconnecting: 'Reconnecting. Your current upload task is being kept.',
    verifying: 'Files uploaded. Checking that every original arrived safely...',
    queued: 'Your design is safely uploaded and waiting for processing.',
    processing: 'Preparing your saved design (__percent__%). No need to upload again.',
    done: 'Your design is saved and ready to add to cart.',
    upload_paused: 'Upload paused. Keep this page open and choose Resume saving design to continue the same task.',
    connection_paused: 'The connection paused. Your current task is kept; try again on this page.',
    upload_stalled: 'The file upload stopped making progress. Keep this page open and resume to keep completed files.',
    upload_slow: 'This file took too long to upload. Keep this page open and resume the same task on a steadier connection.',
    upload_failed: 'The file could not be uploaded. Keep this page open and resume the same task.',
    invalid_authorization: 'The upload authorization is incomplete. Please contact MangoRace before resubmitting.',
    session_expired: 'This upload session has expired or ended. Confirm your design again to start a new upload.',
    sign_in_again: 'Please sign in again, then resume saving your design on this page.',
    session_changed: 'The upload task changed. We need to check its status before continuing.',
    object_conflict: 'The saved file does not match this upload. Please contact MangoRace; your original image has not been changed.',
    processing_uncertain: 'We could not confirm the processing status. Keep this page open and check again; do not start another upload.',
    processing_background: 'Your design is still processing in the background. Check My designs shortly; no need to upload again.',
    processing_failed: 'Your design could not be processed. Your original images have not been changed. Please contact MangoRace before uploading again.',
    verification_paused: 'Your files are uploaded, but verification has not finished. Check again on this page to continue the same task.',
    create_uncertain: 'We could not confirm your upload request. Keep this page open and contact MangoRace before submitting again.',
    canvas_changed: 'The previous design is saved. Your latest canvas changes are not saved yet; confirm again when ready.',
    canvas_preparation_changed: 'The artwork changed while preparing the upload. Please confirm again when you have finished editing.',
    retry_confirm: 'Resume saving design',
    check_status: 'Check saved design status',
    confirm_label: 'Confirm custom design'
  };
  let uploadTranslations = {};
  try { uploadTranslations = JSON.parse(root.querySelector('[data-mango-upload-text]')?.textContent || '{}'); } catch (error) { uploadTranslations = {}; }
  const uploadText = (key, values = {}) => {
    let message = uploadTranslations[key] || uploadDefaults[key] || key;
    Object.entries(values).forEach(([name, value]) => { message = message.split(`__${name}__`).join(String(value)); });
    return message;
  };
  const reportUploadStage = (state) => {
    latestUploadStage = state.stage;
    if (uploadProgressPanel) uploadProgressPanel.hidden = false;
    const percent = state.stage === 'uploading'
      ? Math.min(100, Math.floor((state.loaded || 0) * 100 / Math.max(1, state.total || 0)))
      : Math.min(99, Math.max(0, Math.round(state.progress || 0)));
    if (uploadProgress) {
      if (state.stage === 'paused') {
        uploadProgress.value = Number(uploadProgress.value) || 0;
        uploadProgress.removeAttribute('aria-valuetext');
      } else if (state.stage === 'uploading' || state.stage === 'processing' || state.stage === 'done') {
        uploadProgress.value = state.stage === 'done' ? 100 : percent;
        uploadProgress.setAttribute('aria-valuetext', state.stage === 'done' ? '100%' : `${percent}%`);
      } else {
        uploadProgress.removeAttribute('value');
        uploadProgress.removeAttribute('aria-valuetext');
      }
    }
    const message = state.message || uploadText(state.stage, { percent, loaded: formatFileSize(state.loaded || 0),
      total: formatFileSize(state.total || 0), attempt: state.attempt || 1, limit: state.limit || 3 });
    if (uploadProgressDetail && uploadProgressDetail.textContent !== message) uploadProgressDetail.textContent = message;
    if (!uploadProgressDetail && orderStatus) orderStatus.textContent = message;
  };
  const uploadClient = createCustomizationUploadClient({
    request: (url, options, timeout) => fetchWithCustomerUploadAuth(url, options, timeout),
    apiBase: () => getApiBase(), text: uploadText, onStage: reportUploadStage
  });

  fabric.Object.prototype.set({
    borderColor: '#FF0000',
    cornerColor: '#FF0000',
    cornerStrokeColor: '#ffffff',
    borderDashArray: [18, 12],
    transparentCorners: false,
    cornerSize: 28
  });
  fabricCanvas.upperCanvasEl.tabIndex = 0;
  const onCanvasPointerDown = () => {
    fabricCanvas.upperCanvasEl.focus();
  };
  fabricCanvas.upperCanvasEl.addEventListener('pointerdown', onCanvasPointerDown);

  const getCenterX = () => textureWidth / 2;
  const getCenterY = () => textureHeight / 2;

  const getGuideMode = () => guideModeSelect?.value || 'center';

  const getGuideTargets = () => {
    if (getGuideMode() === 'split') {
      return [
        { x: textureWidth * 0.25, y: getCenterY() },
        { x: textureWidth * 0.75, y: getCenterY() }
      ];
    }
    return [{ x: getCenterX(), y: getCenterY() }];
  };

  const addGuideLine = (className, style) => {
    if (!guideLayer) return;
    const line = document.createElement('div');
    line.className = `mango-customizer__guide-line ${className}`;
    Object.assign(line.style, style);
    guideLayer.appendChild(line);
  };

  const renderGuideOverlay = () => {
    if (!guideLayer) return;
    guideLayer.innerHTML = '';
    if (getGuideMode() === 'split') {
      addGuideLine('mango-customizer__guide-line--vertical', { left: '25%' });
      addGuideLine('mango-customizer__guide-line--vertical', { left: '75%' });
      addGuideLine('mango-customizer__guide-line--horizontal', { top: '50%', left: '0', width: '50%' });
      addGuideLine('mango-customizer__guide-line--horizontal', { top: '50%', left: '50%', width: '50%' });
      return;
    }
    addGuideLine('mango-customizer__guide-line--vertical', { left: '50%' });
    addGuideLine('mango-customizer__guide-line--horizontal', { top: '50%', left: '0', width: '100%' });
  };

  const fitCanvasToStage = () => {
    if (!stage || !fabricCanvas.wrapperEl) return;
    stage.style.width = '100%';
    stage.style.maxWidth = '100%';

    const measuredWidth = stage.getBoundingClientRect().width - 2;
    const availableWidth = measuredWidth > 0 ? measuredWidth : 320;
    displayScale = Math.min(1, availableWidth / textureWidth);
    const visualWidth = Math.max(1, Math.floor(textureWidth * displayScale));
    const visualHeight = Math.max(1, Math.floor(textureHeight * displayScale));

    fabricCanvas.setDimensions({ width: visualWidth, height: visualHeight });
    fabricCanvas.setViewportTransform([displayScale, 0, 0, displayScale, 0, 0]);
    fabricCanvas.wrapperEl.style.position = 'absolute';
    fabricCanvas.wrapperEl.style.left = '0';
    fabricCanvas.wrapperEl.style.top = '0';
    fabricCanvas.wrapperEl.style.width = `${visualWidth}px`;
    fabricCanvas.wrapperEl.style.height = `${visualHeight}px`;
    fabricCanvas.wrapperEl.style.transform = '';
    stage.style.height = `${visualHeight}px`;
    fabricCanvas.calcOffset();
    fabricCanvas.requestRenderAll();
    renderGuideOverlay();
  };

  const showWarning = (message) => {
    if (!warning) return;
    if (warning.textContent === message && warning.hidden === !message) return;
    warning.textContent = message;
    warning.hidden = !message;
  };

  const formatFileSize = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return `${mb >= 10 ? mb.toFixed(1) : mb.toFixed(2)} MB`;
  };

  const updateUploadLimitStatus = () => {
    if (!uploadLimitStatus) return;
    const count = sourceImageStore.size;
    const bytes = [...sourceImageStore.values()].reduce((sum, item) => sum + item.file.size, 0);
    uploadLimitStatus.textContent = uploadText('selected', { count, max: maxImages, bytes: formatFileSize(bytes), limit: formatFileSize(maxTotalImageBytes) });
    uploadLimitStatus.dataset.limitState = bytes > maxTotalImageBytes || count > maxImages ? 'error' : 'normal';
  };

  const applyEditorTemplate = () => {
    templateConfig = getTemplateConfig(root);
    textureWidth = templateConfig.textureWidth;
    textureHeight = templateConfig.textureHeight;
    maxImages = templateConfig.maxImages;
    maxSingleImageBytes = templateConfig.maxSingleImageBytes;
    maxTotalImageBytes = templateConfig.maxTotalImageBytes;
    fabricCanvas.clear();
    sourceImageStore.clear();
    refreshArtworkQuality();
    if (personalNameInput) personalNameInput.value = '';
    if (personalNumberInput) personalNumberInput.value = '';
    textureCanvas.width = textureWidth;
    textureCanvas.height = textureHeight;
    resetConfirmation(`Switched to ${templateConfig.templateName}. Please upload artwork again and confirm the custom design.`);
    setWorkflowStep(section, 2);
    updateUploadLimitStatus();
    renderGuideOverlay();
    fitCanvasToStage();
    updateTextureNow();
  };

  const updateTextureNow = () => {
    textureUpdateFrame = 0;
    root.dataset.fabricObjectCount = String(fabricCanvas.getObjects().filter((object) => (
      object.type !== 'line'
    )).length);
    const wasInteractive = fabricCanvas.interactive;
    const displayWidth = fabricCanvas.width;
    const displayHeight = fabricCanvas.height;
    const viewportTransform = fabricCanvas.viewportTransform?.slice() || [1, 0, 0, 1, 0, 0];
    fabricCanvas.interactive = false;
    fabricCanvas.width = textureWidth;
    fabricCanvas.height = textureHeight;
    fabricCanvas.viewportTransform = [1, 0, 0, 1, 0, 0];
    fabricCanvas.calcViewportBoundaries();
    try {
      textureContext.clearRect(0, 0, textureWidth, textureHeight);
      fabricCanvas.renderCanvas(textureContext, fabricCanvas.getObjects());
    } finally {
      fabricCanvas.interactive = wasInteractive;
      fabricCanvas.width = displayWidth;
      fabricCanvas.height = displayHeight;
      fabricCanvas.viewportTransform = viewportTransform;
      fabricCanvas.calcViewportBoundaries();
    }
    root.dispatchEvent(new CustomEvent('mango:fabric-texture-updated', {
      bubbles: true,
      detail: { canvas: textureCanvas }
    }));
  };

  const updateTexture = () => {
    if (textureUpdateFrame) return;
    textureUpdateFrame = window.requestAnimationFrame(updateTextureNow);
  };

  const keepGuidesOnTop = () => {
    fabricCanvas.requestRenderAll();
  };

  const snapToGuide = (object) => {
    const snapDistance = Math.max(90, 28 / Math.max(displayScale, 0.1));
    const objectCenter = object.getCenterPoint();
    let nextLeft = object.left;
    let nextTop = object.top;
    let snapped = false;
    const targets = getGuideTargets();
    const nearestX = targets.reduce((best, target) => (
      Math.abs(target.x - objectCenter.x) < Math.abs(best.x - objectCenter.x) ? target : best
    ), targets[0]);
    const nearestY = targets.reduce((best, target) => (
      Math.abs(target.y - objectCenter.y) < Math.abs(best.y - objectCenter.y) ? target : best
    ), targets[0]);

    if (nearestX && Math.abs(objectCenter.x - nearestX.x) <= snapDistance) {
      nextLeft += nearestX.x - objectCenter.x;
      snapped = true;
    }
    if (nearestY && Math.abs(objectCenter.y - nearestY.y) <= snapDistance) {
      nextTop += nearestY.y - objectCenter.y;
      snapped = true;
    }

    object.set({ left: nextLeft, top: nextTop });
    object.setCoords();
    root.dataset.fabricSnapState = snapped ? 'snapped' : '';
  };

  const objectExceedsCanvas = (object) => {
    const rect = object.getBoundingRect(true, true);
    return rect.left < 0 || rect.top < 0 || rect.left + rect.width > textureWidth || rect.top + rect.height > textureHeight;
  };

  const resetConfirmation = (message = '') => {
    const wasConfirmed = designConfirmed;
    designConfirmed = false;
    if (addToCartButton) addToCartButton.disabled = true;
    if (orderStatus) {
      orderStatus.textContent = message || (wasConfirmed ? 'Canvas artwork changed. Please confirm the custom design again before adding to cart.' : '');
    }
  };

  const removeActiveObjects = () => {
    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject) {
      showWarning('Please select the artwork you want to remove first.');
      return;
    }

    if (activeObject.type === 'activeSelection') {
      activeObject.getObjects().forEach((object) => fabricCanvas.remove(object));
    } else {
      fabricCanvas.remove(activeObject);
    }

    fabricCanvas.discardActiveObject();
    cleanupUnusedSourceImages();
    updateUploadLimitStatus();
    refreshArtworkQuality();
    fabricCanvas.requestRenderAll();
    resetConfirmation('Artwork removed. Please confirm the custom design again before adding to cart.');
    setWorkflowStep(section, hasArtwork() ? 3 : 2);
    showWarning('');
    updateTexture();
  };

  const setFinishMode = () => {
    const finish = finishModeSelect?.value || 'matte';
    if (finishField) finishField.value = FINISH_LABELS[finish] || FINISH_LABELS.matte;
    root.dispatchEvent(new CustomEvent('mango:finish-changed', {
      bubbles: true,
      detail: { finish }
    }));
  };

  const applyRequestedFinish = () => {
    if (!finishModeSelect) return;
    const requestedFinish = new URLSearchParams(window.location.search).get('finish')?.toLowerCase();
    if (requestedFinish === 'matte' || requestedFinish === 'gloss') {
      finishModeSelect.value = requestedFinish;
    }
  };

  const getSelectedCapOption = () => {
    const option = root.querySelector('[data-mango-cap-option][aria-pressed="true"]')
      || root.querySelector('[data-mango-cap-option]');
    return {
      value: option?.value || '',
      label: option?.dataset.capLabel || option?.textContent?.trim() || 'Standard bottle cap',
      color: option?.dataset.capColor || '#101010',
      sku: option?.dataset.capSku || '',
      variantId: option?.dataset.capVariantId || ''
    };
  };

  const setCapOption = () => {
    root.dispatchEvent(new CustomEvent('mango:cap-changed', {
      bubbles: true,
      detail: getSelectedCapOption()
    }));
  };

  const getSelectedMouthpieceOption = () => {
    const option = root.querySelector('[data-mango-mouthpiece-option][aria-pressed="true"]')
      || root.querySelector('[data-mango-mouthpiece-option]');
    return {
      value: option?.value || '',
      label: option?.dataset.mouthpieceLabel || option?.textContent?.trim() || 'Standard mouthpiece',
      color: option?.dataset.mouthpieceColor || '#F5F2EC',
      sku: option?.dataset.mouthpieceSku || '',
      variantId: option?.dataset.mouthpieceVariantId || ''
    };
  };

  const setMouthpieceOption = () => {
    root.dispatchEvent(new CustomEvent('mango:mouthpiece-changed', {
      bubbles: true,
      detail: getSelectedMouthpieceOption()
    }));
  };

  const isCustomerSelectable = (container) => (
    Number.parseInt(container?.dataset.optionCount || '0', 10) > 1 && !container?.hidden
  );

  const ensureArtworkReady = () => assertArtworkPrintReady(fabricCanvas.getObjects(), sourceImageStore, artworkPendingMessage());
  const canProceed = () => {
    try { ensureArtworkReady(); } catch (error) { showWarning(error.message); return false; }
    const personalization = fabricCanvas.getObjects().find((object) => object.mangoArtworkKind === 'personalization');
    const enteredName = (personalNameInput?.value || '').trim().replace(/\s+/g, ' ').slice(0, 24);
    const enteredNumber = (personalNumberInput?.value || '').trim().toUpperCase().slice(0, 6);
    if (enteredName !== (personalization?.personalizationName || '')
      || enteredNumber !== (personalization?.personalizationNumber || '')) {
      showWarning('Preview your latest name and number before confirming this design.');
      return false;
    }
    if (!ipAgreement?.checked) {
      showWarning('Please accept the design confirmation before saving your bottle.');
      return false;
    }
    return true;
  };

  const isCustomerLoggedIn = () => root.dataset.mangoCustomerLoggedIn === 'true' && Boolean(root.dataset.mangoCustomerId);

  const getLoginUrl = () => {
    const loginPath = root.dataset.mangoLoginUrl
      ? root.dataset.mangoLoginUrl.split('?')[0]
      : '/customer_authentication/login';
    const returnTo = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
    return `${loginPath}?return_to=${returnTo}`;
  };

  const getCustomQuantity = () => {
    const quantity = Number.parseInt(quantityInput?.value || '1', 10);
    return Math.min(99, Math.max(1, Number.isFinite(quantity) ? quantity : 1));
  };

  const getApiBase = () => (
    root.dataset.mangoApiBase
    || window.MANGO_CUSTOMIZER_API_BASE
    || 'https://api.mangorace.com'
  ).replace(/\/$/, '');

  const getAppProxySessionUrl = () => (
    root.dataset.mangoAppProxySessionUrl
    || '/apps/mangorace-customizer/session'
  );

  const getAppProxyDraftUrl = (designId, fileId = '') => {
    const url = new URL(getAppProxySessionUrl(), window.location.origin);
    url.pathname = url.pathname.replace(/\/session\/?$/, '/drafts');
    url.pathname = `${url.pathname.replace(/\/$/, '')}/${encodeURIComponent(designId)}`;
    if (fileId) url.pathname += `/files/${encodeURIComponent(fileId)}`;
    url.search = '';
    return `${url.pathname}${url.search}`;
  };

  const getResumeDesignId = () => new URLSearchParams(window.location.search).get('resume_design') || '';

  const getCartAddUrl = () => (
    root.dataset.mangoCartAddUrl
    || `${window.Shopify?.routes?.root || '/'}cart/add.js`
  );

  const getCartUrl = () => (
    root.dataset.mangoCartUrl
    || `${window.Shopify?.routes?.root || '/'}cart`
  );

  const getCartChangeUrl = () => {
    const route = root.dataset.mangoCartChangeUrl
      || `${window.Shopify?.routes?.root || '/'}cart/change`;
    return route.endsWith('.js') ? route : `${route}.js`;
  };

  const getCartJsonUrl = () => `${getCartUrl().replace(/\/$/, '')}.js`;

  const getCartResponseError = (data) => {
    if (typeof data?.errors === 'string') return data.errors;
    if (Array.isArray(data?.errors)) return data.errors.filter(Boolean).join(' ');
    if (data?.errors && typeof data.errors === 'object') {
      return Object.values(data.errors).flat().filter(Boolean).join(' ');
    }
    return data?.description || data?.message || '';
  };

  const isInvalidCustomCartItem = (item) => {
    const properties = item.properties || {};
    const designId = String(properties['Design ID'] || '').trim();
    const localExpiry = String(properties._mangorace_draft_expiry_local_hour || '').trim();
    const rawExpiry = String(properties._mangorace_draft_expires_at || '').trim();
    const hasCustomIdentity = String(item?.sku || '').startsWith('MR-BOTTLE-CUSTOM')
      || Boolean(designId || localExpiry || rawExpiry);
    if (!hasCustomIdentity) return false;
    const absoluteExpiry = Date.parse(rawExpiry);
    return !designId
      || !localExpiry
      || !rawExpiry
      || !Number.isFinite(absoluteExpiry)
      || absoluteExpiry <= Date.now();
  };

  const removeInvalidCustomDesigns = async (replacementDesignId = '') => {
    const response = await fetch(getCartJsonUrl(), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin'
    });
    if (!response.ok) {
      throw new Error('The cart could not be checked. Please refresh the page and try again.');
    }

    const cart = await response.json().catch(() => null);
    if (!cart || !Array.isArray(cart.items)) {
      throw new Error('The cart could not be checked. Please refresh the page and try again.');
    }
    // A changed proof creates a different Shopify line key. Replace the old
    // line for this design so a valid reconfirmation cannot leave duplicates.
    // Other valid designs and ordinary products must remain untouched.
    const invalidItems = Array.isArray(cart.items)
      ? cart.items.filter((item) => isInvalidCustomCartItem(item)
        || (replacementDesignId && String(item.properties?.['Design ID'] || '').trim() === replacementDesignId))
      : [];

    for (const item of invalidItems) {
      if (typeof item.key !== 'string' || !item.key) {
        throw new Error('The cart item could not be identified. Please open the cart and try again.');
      }
      const removeResponse = await fetch(getCartChangeUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ id: item.key, quantity: 0 })
      });
      const data = await removeResponse.json().catch(() => null);
      if (!removeResponse.ok || !data || !Array.isArray(data.items)) {
        throw new Error(getCartResponseError(data) || 'An expired custom item could not be removed. Please open the cart and try again.');
      }
    }

    return invalidItems.length;
  };

  const normalizeRemoteImageUrl = (value) => {
    if (!value) return '';
    try {
      const url = new URL(value);
      return url.protocol === 'https:' ? url.toString() : '';
    } catch (error) {
      return '';
    }
  };

  const toShopLocalExpiryHour = (value) => {
    const timestamp = Date.parse(value);
    const offset = root.dataset.mangoShopUtcOffset || '+0000';
    const match = offset.match(/^([+-])(\d{2})(\d{2})$/);
    if (!Number.isFinite(timestamp) || !match) return '';

    const direction = match[1] === '-' ? -1 : 1;
    const offsetMinutes = direction * (Number(match[2]) * 60 + Number(match[3]));
    const roundedTimestamp = Math.ceil(timestamp / 3_600_000) * 3_600_000;
    const localDate = new Date(roundedTimestamp + offsetMinutes * 60_000);
    const year = localDate.getUTCFullYear();
    const month = String(localDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(localDate.getUTCDate()).padStart(2, '0');
    const hour = String(localDate.getUTCHours()).padStart(2, '0');
    return `${year}-${month}-${day}T${hour}`;
  };

  const getLocalDraftKey = (accountKey = customerAccountKey) => {
    if (!accountKey) return '';
    return [
    'mangorace-customizer',
    accountKey,
    root.dataset.mangoVariantId || 'no-variant',
    templateConfig.templateId || 'default-template',
    window.location.pathname
    ].join(':');
  };

  const getPendingLocalDraftKey = (handoffId) => `mangorace-customizer:pending:${handoffId}`;

  const createLocalDraftHandoffId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = new Uint32Array(4);
    window.crypto?.getRandomValues?.(bytes);
    return Array.from(bytes, (value) => value.toString(36)).join('-') || `${Date.now()}-${Math.random()}`;
  };

  const readPendingLocalDraftHandoff = () => {
    try {
      return window.sessionStorage.getItem(LOCAL_DRAFT_HANDOFF_STORAGE_KEY) || '';
    } catch (error) {
      return '';
    }
  };

  const writePendingLocalDraftHandoff = (handoffId) => {
    try {
      window.sessionStorage.setItem(LOCAL_DRAFT_HANDOFF_STORAGE_KEY, handoffId);
      return true;
    } catch (error) {
      return false;
    }
  };

  const clearPendingLocalDraftHandoff = () => {
    try {
      window.sessionStorage.removeItem(LOCAL_DRAFT_HANDOFF_STORAGE_KEY);
    } catch (error) {
      // A blocked sessionStorage must not stop a signed-in customer from continuing.
    }
  };

  const requestSnapshot = () => new Promise((resolve) => {
    const timeout = window.setTimeout(() => {
      root.removeEventListener('mango:3d-snapshot-ready', handleSnapshot);
      resolve(latestSnapshotDataUrl);
    }, 1400);

    function handleSnapshot(event) {
      window.clearTimeout(timeout);
      latestSnapshotDataUrl = event.detail?.dataUrl || '';
      resolve(latestSnapshotDataUrl);
    }

    root.addEventListener('mango:3d-snapshot-ready', handleSnapshot, { once: true });
    root.dispatchEvent(new CustomEvent('mango:request-3d-snapshot', { bubbles: true }));
  });

  const createArtworkPreviewDataUrl = () => {
    const previewCanvas = document.createElement('canvas');
    const previewWidth = 900;
    const previewHeight = Math.round((textureHeight / textureWidth) * previewWidth);
    previewCanvas.width = previewWidth;
    previewCanvas.height = previewHeight;
    const previewContext = previewCanvas.getContext('2d');
    previewContext.clearRect(0, 0, previewWidth, previewHeight);
    previewContext.drawImage(textureCanvas, 0, 0, previewWidth, previewHeight);
    return previewCanvas.toDataURL('image/png');
  };

  const dataUrlToBlob = async (dataUrl) => {
    if (!dataUrl) return null;
    const response = await fetch(dataUrl);
    if (!response.ok) throw new Error('Preview image preparation failed. Please try again.');
    return await response.blob();
  };

  const removeEmbeddedImageData = (objects = []) => {
    objects.forEach((object) => {
      if (object.objects?.length) removeEmbeddedImageData(object.objects);
      if (object.type === 'image' && object.sourceImageId) {
        object.src = `source-image://${object.sourceImageId}`;
      }
    });
  };

  const saveLocalDraftBeforeLogin = async () => {
    ensureArtworkReady();
    const handoffId = createLocalDraftHandoffId();
    if (!writePendingLocalDraftHandoff(handoffId)) {
      throw new Error('Browser session storage is unavailable.');
    }
    cleanupUnusedSourceImages();
    const fabricJson = fabricCanvas.toJSON(savedImageProperties);
    removeEmbeddedImageData(fabricJson.objects);
    const sourceImageIds = [...collectSourceImageIds(fabricJson.objects)];
    const sourceImages = sourceImageIds
      .map((id) => sourceImageStore.get(id))
      .filter(Boolean)
      .map((item) => ({
        id: item.id,
        file: item.file,
        name: item.file.name,
        type: item.file.type,
        lastModified: item.file.lastModified
      }));

    await saveLocalDraftRecord({
      key: getPendingLocalDraftKey(handoffId),
      version: 2,
      accountKey: '',
      pendingHandoffId: handoffId,
      savedAt: Date.now(),
      path: `${window.location.pathname}${window.location.search}`,
      variantId: root.dataset.mangoVariantId || '',
      templateId: templateConfig.templateId,
      fabricJson,
      sourceImages,
      finishMode: finishModeSelect?.value || 'matte',
      guideMode: getGuideMode(),
      capValue: getSelectedCapOption().value,
      mouthpieceValue: getSelectedMouthpieceOption().value,
      quantity: getCustomQuantity(),
      ipAgreementAccepted: Boolean(ipAgreement?.checked)
    });
  };

  const requireCustomerLogin = async () => {
    if (isCustomerLoggedIn()) return true;
    const message = 'Please sign in before saving a custom design. Your current canvas has been saved in this browser.';
    showWarning(message);
    if (orderStatus) orderStatus.textContent = message;

    try {
      await saveLocalDraftBeforeLogin();
    } catch (error) {
      const fallbackMessage = 'Please sign in before saving a custom design. If the browser cannot save this temporary canvas, keep this tab open and sign in in another tab.';
      showWarning(fallbackMessage);
      if (orderStatus) orderStatus.textContent = fallbackMessage;
    }

    window.location.href = getLoginUrl();
    return false;
  };

  const fetchWithTimeout = async (url, options, timeoutMs = 300000) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal
      });
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const getCustomerUploadToken = async () => {
    if (customerUploadToken && Date.now() < customerUploadTokenExpiresAt - 30_000) {
      return customerUploadToken;
    }

    const response = await fetchWithTimeout(getAppProxySessionUrl(), {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin'
    }, 15_000);
    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.token) {
      throw new Error(data.message || 'Shopify could not verify your customer login. Please refresh the page and sign in again.');
    }

    const accountKey = typeof data.customer?.accountKey === 'string' ? data.customer.accountKey : '';
    if (!accountKey) {
      throw new Error('Shopify could not verify your customer account. Please refresh the page and sign in again.');
    }

    customerUploadToken = data.token;
    customerUploadTokenExpiresAt = Date.now() + (Number(data.expiresIn) || 900) * 1000;
    customerAccountKey = accountKey;
    return customerUploadToken;
  };

  const fetchWithCustomerUploadAuth = async (url, options = {}, timeoutMs = 300000) => {
    const request = async () => {
      let token;
      try { token = await getCustomerUploadToken(); }
      catch (error) { error.requestNotSent = true; throw error; }
      return await fetchWithTimeout(url, {
        ...options,
        headers: {
          ...(options.headers || {}),
          Authorization: `Bearer ${token}`
        }
      }, timeoutMs);
    };
    let response = await request();
    if (response.status === 401) {
      customerUploadToken = '';
      customerUploadTokenExpiresAt = 0;
      response = await request();
    }
    return response;
  };

  const getCustomerAccountKey = async () => {
    if (!isCustomerLoggedIn()) return '';
    if (!customerAccountKey) await getCustomerUploadToken();
    return customerAccountKey;
  };

  const collectSourceImageIds = (objects = [], ids = new Set()) => {
    objects.forEach((object) => {
      if (object.objects?.length) collectSourceImageIds(object.objects, ids);
      if (object.sourceImageId) ids.add(object.sourceImageId);
    });
    return ids;
  };

  const cleanupUnusedSourceImages = () => {
    const currentIds = collectSourceImageIds(fabricCanvas.toJSON(['sourceImageId']).objects);
    [...sourceImageStore.keys()].forEach((id) => {
      if (!currentIds.has(id)) sourceImageStore.delete(id);
    });
    updateUploadLimitStatus();
  };

  const getCurrentSourceImageBytes = () => {
    cleanupUnusedSourceImages();
    return [...sourceImageStore.values()].reduce((sum, item) => sum + item.file.size, 0);
  };

  const canAcceptUploadFiles = (files) => {
    cleanupUnusedSourceImages();
    if (!files.length) return false;

    const invalidFile = files.find((file) => !/^image\/(png|jpeg)$/.test(file.type));
    if (invalidFile) {
      showWarning('Please upload a PNG or JPG image.');
      return false;
    }

    const oversizedFile = files.find((file) => file.size > maxSingleImageBytes);
    if (oversizedFile) {
      showWarning(`"${oversizedFile.name}" exceeds ${formatFileSize(maxSingleImageBytes)}. Please compress it before uploading.`);
      return false;
    }

    const remainingSlots = maxImages - sourceImageStore.size;
    if (files.length > remainingSlots) {
      showWarning(`You can upload ${Math.max(remainingSlots, 0)} more images, but selected ${files.length} images, Please remove unused artwork first.`);
      return false;
    }

    const incomingBytes = files.reduce((sum, file) => sum + file.size, 0);
    const currentBytes = getCurrentSourceImageBytes();
    const projectedBytes = currentBytes + incomingBytes;
    if (projectedBytes > maxTotalImageBytes) {
      showWarning(`The total original image size for one design cannot exceed ${formatFileSize(maxTotalImageBytes)}. Current: ${formatFileSize(currentBytes)}, selected this time: ${formatFileSize(incomingBytes)}.`);
      return false;
    }

    return true;
  };

  const restoreSourceImage = (item) => {
    if (!item?.file) return null;
    const file = item.file instanceof File
      ? item.file
      : new File([item.file], item.name || 'custom-artwork.png', {
        type: item.type || item.file.type || 'image/png',
        lastModified: item.lastModified || Date.now()
      });
    return {
      id: item.id,
      file
    };
  };

  const loadDraftIntoCanvas = ({
    fabricJson: rawFabricJson,
    sourceImages = [],
    finishMode,
    guideMode,
    capValue,
    mouthpieceValue,
    quantity,
    ipAgreementAccepted,
    designId,
    expiresAt,
    message
  }) => {
    const restoredImages = new Map();
    sourceImages.forEach((item) => {
      const sourceImage = restoreSourceImage(item);
      if (sourceImage) restoredImages.set(sourceImage.id, sourceImage);
    });

    const fabricJson = rawFabricJson ? JSON.parse(JSON.stringify(rawFabricJson)) : null;
    if (!fabricJson?.objects?.length) return false;

    const sourceImageIds = [...collectSourceImageIds(fabricJson.objects)];
    if (sourceImageIds.some((id) => !restoredImages.has(id))) {
      throw new Error('Some original artwork files are unavailable. This design cannot be restored safely.');
    }

    const applyImageSources = (objects = []) => {
      objects.forEach((object) => {
        if (object.objects?.length) applyImageSources(object.objects);
        if (object.type === 'image' && object.sourceImageId) {
          const sourceImage = restoredImages.get(object.sourceImageId);
          if (sourceImage?.file) {
            object.src = URL.createObjectURL(sourceImage.file);
          }
        }
      });
    };
    applyImageSources(fabricJson.objects);

    if (finishModeSelect && finishMode) finishModeSelect.value = finishMode;
    if (guideModeSelect && guideMode) guideModeSelect.value = guideMode;
    if (quantityInput && quantity) quantityInput.value = String(quantity);
    if (ipAgreement) ipAgreement.checked = Boolean(ipAgreementAccepted);
    if (capValue) {
      root.querySelectorAll('[data-mango-cap-option]').forEach((item) => {
        item.setAttribute('aria-pressed', String(item.value === capValue));
      });
    }
    if (mouthpieceValue) {
      root.querySelectorAll('[data-mango-mouthpiece-option]').forEach((item) => {
        item.setAttribute('aria-pressed', String(item.value === mouthpieceValue));
      });
    }

    if (designId) {
      root.dataset.mangoDesignId = designId;
      editingSavedDesign = true;
    }
    if (expiresAt) root.dataset.mangoDraftExpiresAt = expiresAt;
    if (designIdField && designId) designIdField.value = designId;

    sourceImageStore.clear();
    restoredImages.forEach((item, id) => sourceImageStore.set(id, item));

    fabricCanvas.loadFromJSON(fabricJson, () => {
      fabricCanvas.getObjects().forEach((object) => {
        object.set({
          padding: 8,
          selectable: true,
          evented: true
        });
        object.setCoords();
      });
      const personalization = fabricCanvas.getObjects().find((object) => object.mangoArtworkKind === 'personalization');
      if (personalNameInput) personalNameInput.value = personalization?.personalizationName || '';
      if (personalNumberInput) personalNumberInput.value = personalization?.personalizationNumber || '';
      fabricCanvas.discardActiveObject();
      fabricCanvas.requestRenderAll();
      setFinishMode();
      setCapOption();
      setMouthpieceOption();
      renderGuideOverlay();
      updateUploadLimitStatus();
      updateTextureNow();
      refreshArtworkQuality();
      resetConfirmation(message);
      setWorkflowStep(section, 3);
      showWarning(message);
    });

    return true;
  };

  const restoreLocalDraftIfAvailable = async () => {
    if (!isCustomerLoggedIn()) return;

    const accountKey = await getCustomerAccountKey();
    const localDraftKey = getLocalDraftKey(accountKey);
    if (!localDraftKey) return;

    const handoffId = readPendingLocalDraftHandoff();
    let draft;
    try {
      if (handoffId) {
        const pendingDraftKey = getPendingLocalDraftKey(handoffId);
        const pendingDraft = await readLocalDraftRecord(pendingDraftKey);
        if (pendingDraft?.pendingHandoffId === handoffId && !pendingDraft.accountKey) {
          draft = {
            ...pendingDraft,
            key: localDraftKey,
            accountKey,
            pendingHandoffId: ''
          };
          await saveLocalDraftRecord(draft);
          await deleteLocalDraftRecord(pendingDraftKey);
          clearPendingLocalDraftHandoff();
        } else {
          clearPendingLocalDraftHandoff();
        }
      }
      if (!draft) draft = await readLocalDraftRecord(localDraftKey);
    } catch (error) {
      return;
    }

    if (!draft) return;
    if (!draft.savedAt || Date.now() - draft.savedAt > LOCAL_DRAFT_TTL_MS) {
      await deleteLocalDraftRecord(localDraftKey);
      return;
    }
    if (draft.accountKey !== accountKey) {
      return;
    }

    loadDraftIntoCanvas({
      fabricJson: draft.fabricJson,
      sourceImages: draft.sourceImages,
      finishMode: draft.finishMode,
      guideMode: draft.guideMode,
      capValue: draft.capValue,
      mouthpieceValue: draft.mouthpieceValue,
      quantity: draft.quantity,
      ipAgreementAccepted: draft.ipAgreementAccepted,
      message: 'Your canvas was restored after sign-in. Please review it, then confirm the custom design.'
    });
  };

  const restoreRemoteDraftIfRequested = async () => {
    const designId = getResumeDesignId();
    if (!designId) return false;
    if (!isCustomerLoggedIn()) {
      showWarning('Please sign in before continuing this saved design.');
      return false;
    }

    const response = await fetchWithTimeout(getAppProxyDraftUrl(designId), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }, 60_000);
    const draft = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(draft.message || 'Saved design loading failed. Please try again later.');
    }

    const sourceImages = await Promise.all((draft.sourceImages || []).map(async (item) => {
      const imageResponse = await fetchWithTimeout(getAppProxyDraftUrl(designId, item.fileId), {
        method: 'GET',
        credentials: 'same-origin'
      }, 120_000);
      if (!imageResponse.ok) throw new Error('One of the original artwork files could not be restored.');
      const blob = await imageResponse.blob();
      return {
        id: item.id,
        file: new File([blob], item.name || 'custom-artwork.png', {
          type: item.mimeType || blob.type || 'image/png',
          lastModified: Date.now()
        })
      };
    }));

    const restored = loadDraftIntoCanvas({
      fabricJson: draft.fabricJson,
      sourceImages,
      ...draft.settings,
      designId: draft.designId,
      expiresAt: draft.expiresAt,
      message: 'Your saved design was restored. Please review it, then confirm the custom design again.'
    });
    if (!restored) throw new Error('This saved design does not contain usable canvas data.');
    return true;
  };

  const captureUploadSnapshot = () => {
    ensureArtworkReady();
    const finishMode = finishModeSelect?.value || 'matte';
    const finishType = FINISH_LABELS[finishMode] || FINISH_LABELS.matte;
    const capOption = getSelectedCapOption();
    const mouthpieceOption = getSelectedMouthpieceOption();
    const fabricJson = fabricCanvas.toJSON(savedImageProperties);
    removeEmbeddedImageData(fabricJson.objects);
    const sourceImageIds = [...collectSourceImageIds(fabricJson.objects)];
    const sourceImages = sourceImageIds
      .map((id) => sourceImageStore.get(id))
      .filter(Boolean)
      .map((item) => ({ id: item.id, file: item.file }));
    const totalSourceImageBytes = sourceImages.reduce((sum, item) => sum + item.file.size, 0);

    if (sourceImages.length > maxImages) {
      throw new Error(`One design can use up to ${maxImages} images.`);
    }
    if (sourceImageIds.length !== sourceImages.length) {
      throw new Error('Artwork data is incomplete. Please remove the abnormal artwork and upload again.');
    }
    if (totalSourceImageBytes > maxTotalImageBytes) {
      throw new Error(`The total original image size for one design cannot exceed ${formatFileSize(maxTotalImageBytes)}.`);
    }

    const metadata = {
      ...(editingSavedDesign && root.dataset.mangoDesignId ? { designId: root.dataset.mangoDesignId } : {}),
      finishType,
      finishMode,
      capOption: capOption.value,
      capLabel: capOption.label,
      capSku: capOption.sku,
      capColor: capOption.color,
      capVariantId: capOption.variantId,
      mouthpieceOption: mouthpieceOption.value,
      mouthpieceLabel: mouthpieceOption.label,
      mouthpieceSku: mouthpieceOption.sku,
      mouthpieceColor: mouthpieceOption.color,
      mouthpieceVariantId: mouthpieceOption.variantId,
      guideMode: getGuideMode(),
      ipAgreementAccepted: Boolean(ipAgreement?.checked),
      sku: templateConfig.sku
    };
    return { fabricJson, sourceImages, metadata, fingerprint: JSON.stringify({ fabricJson, metadata,
      sourceImages: sourceImages.map((item) => ({ id: item.id, name: item.file.name, size: item.file.size, type: item.file.type, lastModified: item.file.lastModified })) }) };
  };

  const matchesUploadSnapshot = (session, snapshot) => session.snapshot.fingerprint === snapshot.fingerprint
    && session.snapshot.sourceImages.length === snapshot.sourceImages.length
    && session.snapshot.sourceImages.every((item, index) => item.file === snapshot.sourceImages[index].file);

  const applySavedUpload = async (session, data) => {
    let canvasChanged = true;
    try { canvasChanged = !matchesUploadSnapshot(session, captureUploadSnapshot()); } catch (error) { canvasChanged = true; }
    root.dataset.mangoDesignId = data.designId;
    root.dataset.mangoDraftExpiresAt = data.expiresAt || '';
    if (designIdField) designIdField.value = data.designId;
    if (finishField) finishField.value = session.snapshot.metadata.finishType;
    if (snapshotField) snapshotField.value = normalizeRemoteImageUrl(data.files?.previewImageUrl);
    if (artworkField) artworkField.value = normalizeRemoteImageUrl(data.files?.artworkPreviewUrl);
    if (!canvasChanged) await deleteLocalDraftRecord(getLocalDraftKey());
    return { ...data, canvasChanged };
  };

  const submitCustomization = async () => {
    if (uploadCreateUncertain) throw new Error(uploadText('create_uncertain'));
    const snapshot = captureUploadSnapshot();
    if (uploadSession && !matchesUploadSnapshot(uploadSession, snapshot)) {
      if (!uploadSession.result) {
        const previous = await uploadClient.cancelForChangedDesign(uploadSession);
        if (previous.result) return await applySavedUpload(uploadSession, previous.result);
      }
      uploadSession = null;
    }
    if (!uploadSession) {
      reportUploadStage({ stage: 'preparing' });
      updateTextureNow();
      const artworkPreviewDataUrl = createArtworkPreviewDataUrl();
      const previewImageDataUrl = await requestSnapshot();
      const fabricBlob = new Blob([JSON.stringify(snapshot.fabricJson)], { type: 'application/json' });
      const artworkPreviewBlob = await dataUrlToBlob(artworkPreviewDataUrl);
      const previewImageBlob = await dataUrlToBlob(previewImageDataUrl);
      if (!matchesUploadSnapshot({ snapshot }, captureUploadSnapshot())) throw new Error(uploadText('canvas_preparation_changed'));
      reportUploadStage({ stage: 'creating' });
      await getCustomerUploadToken();
      let createResponse;
      try {
        createResponse = await fetchWithCustomerUploadAuth(`${getApiBase()}/customizations/customer/uploads`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...snapshot.metadata,
            sourceImages: snapshot.sourceImages.map((item) => ({
              id: item.id,
              name: item.file.name,
              size: item.file.size,
              mimeType: item.file.type
            })),
            fabricJson: {
              name: 'fabric.json',
              size: fabricBlob.size,
              mimeType: 'application/json'
            },
            ...(previewImageBlob ? {
              previewImage: {
                name: '3d-preview.png',
                size: previewImageBlob.size,
                mimeType: previewImageBlob.type || 'image/png'
              }
            } : {}),
            artworkPreviewImage: {
              name: 'artwork-preview.png',
              size: artworkPreviewBlob.size,
              mimeType: artworkPreviewBlob.type || 'image/png'
            }
          })
        }, 30_000);
      } catch (error) {
        if (error.requestNotSent) throw error;
        uploadCreateUncertain = true;
        throw new Error(uploadText('create_uncertain'));
      }
      const uploadJob = await createResponse.json().catch(() => ({}));
      if (!createResponse.ok) {
        if (createResponse.status >= 500 || createResponse.status === 408) {
          uploadCreateUncertain = true;
          throw new Error(uploadText('create_uncertain'));
        }
        throw new Error(uploadJob.message || 'The upload queue is temporarily unavailable. Please try again later.');
      }
      if (!uploadJob.jobId || !Array.isArray(uploadJob.uploads)) {
        uploadCreateUncertain = true;
        throw new Error(uploadText('create_uncertain'));
      }
      const uploadByKind = new Map();
      (uploadJob.uploads || []).forEach((upload) => {
        const key = upload.kind === 'SOURCE_IMAGE'
          ? `${upload.kind}:${upload.sourceImageId}`
          : upload.kind;
        uploadByKind.set(key, upload);
      });
      const uploadTasks = [
        ...snapshot.sourceImages.map((item) => ({
          upload: uploadByKind.get(`SOURCE_IMAGE:${item.id}`),
          blob: item.file,
          fileName: item.file.name
        })),
        { upload: uploadByKind.get('FABRIC_JSON'), blob: fabricBlob, fileName: 'fabric.json' },
        ...(previewImageBlob ? [{
          upload: uploadByKind.get('PREVIEW_3D'),
          blob: previewImageBlob,
          fileName: '3d-preview.png'
        }] : []),
        {
          upload: uploadByKind.get('ARTWORK_PREVIEW'),
          blob: artworkPreviewBlob,
          fileName: 'artwork-preview.png'
        }
      ];
      uploadSession = { jobId: uploadJob.jobId, expiresAt: uploadJob.expiresAt, phase: 'CREATED', snapshot,
        tasks: uploadTasks.map((task) => ({ ...task, done: false, loaded: 0, needsVerification: false })), hasStarted: false };
    }
    const session = uploadSession;
    const inspect = session.hasStarted;
    session.hasStarted = true;
    try {
      const data = await uploadClient.resume(session, { inspect });
      return await applySavedUpload(session, data);
    } catch (error) {
      if (error.code === 'TERMINAL' || error.code === 'EXPIRED') uploadSession = null;
      throw error;
    }
  };

  const confirmDesign = async () => {
    if (submissionInFlight) return;
    try { ensureArtworkReady(); } catch (error) { showWarning(error.message); return; }
    submissionInFlight = true;
    try {
      if (!(await requireCustomerLogin())) return;
      if (designConfirmed && root.dataset.mangoDesignId) {
        if (orderStatus) orderStatus.textContent = `This design is already saved. Design ID: ${root.dataset.mangoDesignId}.`;
        return;
      }
      if (!canProceed()) return;
      setWorkflowStep(section, 4);
      if (confirmButton) confirmButton.disabled = true;
      if (orderStatus) {
        orderStatus.textContent = uploadText('preparing');
      }

      try {
        const data = await submitCustomization();
        if (data.canvasChanged) {
          designConfirmed = false;
          if (addToCartButton) addToCartButton.disabled = true;
          reportUploadStage({ stage: 'paused', message: uploadText('canvas_changed') });
          showWarning(uploadText('canvas_changed'));
          if (orderStatus) orderStatus.textContent = uploadText('canvas_changed');
          return;
        }
        designConfirmed = true;
        setWorkflowStep(section, 5);
        if (addToCartButton) addToCartButton.disabled = false;
        if (orderStatus) {
          orderStatus.textContent = `Custom design saved. Design ID: ${data.designId}. Next, you can add it to cart.`;
        }
        showWarning('');
        reportUploadStage({ stage: 'done' });
      } catch (error) {
        designConfirmed = false;
        setWorkflowStep(section, 4);
        if (addToCartButton) addToCartButton.disabled = true;
        const message = error.name === 'AbortError'
          ? uploadText(latestUploadStage === 'verifying' ? 'verification_paused' : 'connection_paused')
          : (error.message || 'Custom design submission failed. Please try again later.');
        reportUploadStage({ stage: 'paused', message });
        showWarning(message);
        if (orderStatus) orderStatus.textContent = message;
      } finally {
        if (confirmButton) {
          confirmButton.disabled = false;
          confirmButton.textContent = uploadText(uploadSession && !designConfirmed
            ? (['QUEUED', 'PROCESSING', 'VERIFYING'].includes(uploadSession.phase) ? 'check_status' : 'retry_confirm') : 'confirm_label');
        }
      }
    } finally {
      submissionInFlight = false;
      refreshArtworkQuality();
    }
  };

  const onConfirmDesignClick = () => {
    void confirmDesign();
  };

  const addToCart = async () => {
    if (!designConfirmed || !canProceed()) return;
    setWorkflowStep(section, 5);
    const variantId = root.dataset.mangoVariantId;
    const designId = root.dataset.mangoDesignId;
    if (!variantId) {
      showWarning('Please select the custom bottle product in Shopify theme settings first.');
      return;
    }
    if (!designId) {
      showWarning('Please confirm the custom design first to generate a design ID before adding to cart.');
      return;
    }
    const draftExpiresAt = root.dataset.mangoDraftExpiresAt || '';
    const draftExpiryLocalHour = toShopLocalExpiryHour(draftExpiresAt);
    if (!draftExpiresAt || Date.parse(draftExpiresAt) <= Date.now()) {
      showWarning('This saved design has expired. Please confirm the design again before adding it to cart.');
      designConfirmed = false;
      if (addToCartButton) addToCartButton.disabled = true;
      return;
    }
    if (!draftExpiryLocalHour) {
      showWarning('The design expiry information could not be generated. Please refresh the page and confirm the design again.');
      return;
    }

    if (addToCartButton) addToCartButton.disabled = true;
    if (orderStatus) orderStatus.textContent = 'Adding to cart...';

    try {
      const proofResponse = await fetchWithCustomerUploadAuth(
        `${getApiBase()}/customizations/customer/designs/${encodeURIComponent(designId)}/checkout-proof`,
        {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ variantId: String(variantId) })
        },
        30_000
      );
      const proofData = await proofResponse.json().catch(() => ({}));
      if (!proofResponse.ok || typeof proofData.checkoutProof !== 'string' || !proofData.checkoutProof.trim()
        || !proofData.expiryLocalHour) {
        throw new Error(proofData.message || 'Please reopen your saved design and confirm it before adding it to your cart.');
      }
      const removedItemCount = await removeInvalidCustomDesigns(designId);
      if (removedItemCount > 0 && orderStatus) {
        orderStatus.textContent = 'Cart updated. Adding the latest version of your design...';
      }

      const finishMode = finishModeSelect?.value || 'matte';
      const finishType = FINISH_LABELS[finishMode] || FINISH_LABELS.matte;
      const capOption = getSelectedCapOption();
      const mouthpieceOption = getSelectedMouthpieceOption();
      const artworkImageUrl = normalizeRemoteImageUrl(artworkField?.value);
      const previewImageUrl = normalizeRemoteImageUrl(snapshotField?.value) || artworkImageUrl;
      const properties = {
        'Design ID': designId,
        'Custom Product': templateConfig.templateName,
        'Custom Template': getCustomerVisibleTemplateId(templateConfig.templateId),
        'Custom Quantity': String(getCustomQuantity()),
        Finish: finishType,
        'Guide Mode': getGuideMode(),
        'Customizer Status': 'Confirmed',
        _mangorace_draft_expires_at: draftExpiresAt,
        _mangorace_draft_expiry_local_hour: proofData.expiryLocalHour,
        _mangorace_checkout_proof: proofData.checkoutProof,
        _mangorace_cap_label: capOption.label,
        _mangorace_cap_sku: capOption.sku,
        _mangorace_cap_color: capOption.color,
        _mangorace_cap_variant_id: capOption.variantId,
        _mangorace_mouthpiece_label: mouthpieceOption.label,
        _mangorace_mouthpiece_sku: mouthpieceOption.sku,
        _mangorace_mouthpiece_color: mouthpieceOption.color,
        _mangorace_mouthpiece_variant_id: mouthpieceOption.variantId
      };
      const personalization = fabricCanvas.getObjects().find((object) => object.mangoArtworkKind === 'personalization');
      if (personalization?.personalizationName) properties['Personalized Name'] = personalization.personalizationName;
      if (personalization?.personalizationNumber) properties['Personalized Number'] = personalization.personalizationNumber;
      if (isCustomerSelectable(capOptions)) properties['Cap Color'] = capOption.label;
      if (isCustomerSelectable(mouthpieceOptions)) {
        properties['Mouthpiece Color'] = mouthpieceOption.label;
      }
      if (previewImageUrl) properties._mangorace_preview_image = previewImageUrl;
      if (artworkImageUrl) properties._mangorace_artwork_image = artworkImageUrl;

      const response = await fetch(getCartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: getCustomQuantity(),
            properties
          }]
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(getCartResponseError(data) || 'Add to cart failed. Please try again later.');
      }
      if (orderStatus) {
        orderStatus.textContent = `Added to cart. Design ID: ${designId}.`;
      }
      window.location.href = getCartUrl();
    } catch (error) {
      if (addToCartButton) addToCartButton.disabled = false;
      const message = error.message || 'Add to cart failed. Please try again later.';
      showWarning(message);
      if (orderStatus) orderStatus.textContent = message;
    }
  };

  fabricCanvas.on('object:moving', (event) => {
    setWorkflowStep(section, 3);
    resetConfirmation('Canvas artwork moved. Please confirm the custom design again before adding to cart.');
    if (event.target) {
      snapToGuide(event.target);
      showWarning(objectExceedsCanvas(event.target) ? 'Tip: the current image is outside the custom canvas. Please scale it down or drag it back inside.' : '');
    }
  });
  fabricCanvas.on('object:scaling', (event) => {
    setWorkflowStep(section, 3);
    resetConfirmation('Canvas artwork scaled. Please confirm the custom design again before adding to cart.');
    if (event.target) {
      showWarning(objectExceedsCanvas(event.target) ? 'Tip: the current image is outside the custom canvas. Please scale it down or drag it back inside.' : '');
    }
  });
  fabricCanvas.on('object:modified', () => {
    setWorkflowStep(section, 3);
    resetConfirmation('Canvas artwork changed. Please confirm the custom design again before adding to cart.');
    keepGuidesOnTop();
    updateTexture();
  });
  fabricCanvas.on('object:added', () => {
    setWorkflowStep(section, 3);
    resetConfirmation();
    keepGuidesOnTop();
    updateTexture();
  });

  const touchPointInCanvas = (touch) => {
    const rect = fabricCanvas.upperCanvasEl.getBoundingClientRect();
    return new fabric.Point(
      (touch.clientX - rect.left) / Math.max(displayScale, 0.001),
      (touch.clientY - rect.top) / Math.max(displayScale, 0.001)
    );
  };

  const touchMidpointInCanvas = (touches) => new fabric.Point(
    (touchPointInCanvas(touches[0]).x + touchPointInCanvas(touches[1]).x) / 2,
    (touchPointInCanvas(touches[0]).y + touchPointInCanvas(touches[1]).y) / 2
  );

  const touchDistance = (touches) => Math.hypot(
    touches[1].clientX - touches[0].clientX,
    touches[1].clientY - touches[0].clientY
  );

  const findPinchTarget = (touches) => {
    const activeObject = fabricCanvas.getActiveObject();
    // A selected object is an explicit choice. Otherwise the gesture must touch artwork.
    if (activeObject) return activeObject;

    const points = [
      touchPointInCanvas(touches[0]),
      touchPointInCanvas(touches[1]),
      touchMidpointInCanvas(touches)
    ];
    const objects = fabricCanvas.getObjects().filter((object) => object.selectable !== false && object.visible !== false);
    const candidates = objects.slice().reverse();
    return candidates.find((object) => {
      const bounds = object.getBoundingRect(true, true);
      return points.some((point) => (
        point.x >= bounds.left
        && point.x <= bounds.left + bounds.width
        && point.y >= bounds.top
        && point.y <= bounds.top + bounds.height
      ));
    }) || null;
  };

  const beginPinchGesture = (event) => {
    if (event.touches.length !== 2) return;
    const target = findPinchTarget(event.touches);
    if (!target) return;
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();

    fabricCanvas._currentTransform = null;
    fabricCanvas.setActiveObject(target);
    const startDistance = touchDistance(event.touches);
    const startWidth = Math.max(1, target.getScaledWidth());
    const startHeight = Math.max(1, target.getScaledHeight());
    const minimumVisualSize = 36 / Math.max(displayScale, 0.001);
    const maximumLogicalSize = Math.max(textureWidth, textureHeight) * 4;
    pinchGesture = {
      target,
      startDistance: Math.max(1, startDistance),
      startScaleX: target.scaleX || 1,
      startScaleY: target.scaleY || 1,
      minFactor: Math.max(minimumVisualSize / startWidth, minimumVisualSize / startHeight),
      maxFactor: Math.min(maximumLogicalSize / startWidth, maximumLogicalSize / startHeight),
      changed: false
    };
    fabricCanvas.requestRenderAll();
  };

  const movePinchGesture = (event) => {
    if (!pinchGesture || event.touches.length !== 2) return;
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();

    const rawFactor = touchDistance(event.touches) / pinchGesture.startDistance;
    const lowerBound = Math.min(pinchGesture.minFactor, pinchGesture.maxFactor);
    const upperBound = Math.max(pinchGesture.minFactor, pinchGesture.maxFactor);
    const factor = Math.min(upperBound, Math.max(lowerBound, rawFactor));
    pinchGesture.target.set({
      scaleX: pinchGesture.startScaleX * factor,
      scaleY: pinchGesture.startScaleY * factor
    });
    pinchGesture.target.setCoords();
    if (!pinchGesture.changed && Math.abs(factor - 1) > 0.01) {
      pinchGesture.changed = true;
      resetConfirmation('Canvas artwork scaled. Please confirm the custom design again before adding to cart.');
    }
    showWarning(objectExceedsCanvas(pinchGesture.target) ? 'Tip: the current image is outside the custom canvas. Please pinch it smaller or move it back inside.' : '');
    fabricCanvas.requestRenderAll();
  };

  const endPinchGesture = (event) => {
    if (!pinchGesture || event.touches.length >= 2) return;
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();

    const completedGesture = pinchGesture;
    pinchGesture = null;
    fabricCanvas._currentTransform = null;
    completedGesture.target.setCoords();
    keepGuidesOnTop();
    if (completedGesture.changed) updateTexture();
  };

  fabricCanvas.upperCanvasEl.addEventListener('touchstart', beginPinchGesture, { passive: false, capture: true });
  fabricCanvas.upperCanvasEl.addEventListener('touchmove', movePinchGesture, { passive: false, capture: true });
  fabricCanvas.upperCanvasEl.addEventListener('touchend', endPinchGesture, { passive: false, capture: true });
  fabricCanvas.upperCanvasEl.addEventListener('touchcancel', endPinchGesture, { passive: false, capture: true });

  let canvasInteractionActive = false;
  const pendingPixelSwaps = new Set();
  const finishCanvasInteraction = () => {
    canvasInteractionActive = false;
    for (const run of [...pendingPixelSwaps]) run();
  };
  const refreshArtworkQuality = (tasks = artworkTasks) => {
    artworkTasks = tasks;
    const active = [...tasks.values()].filter((task) => fabricCanvas.getObjects().includes(task.image));
    const failed = active.some((task) => task.state === 'error');
    const loading = active.filter((task) => task.state === 'loading');
    let ready = true;
    try { ensureArtworkReady(); } catch { ready = false; }
    if (confirmButton) confirmButton.disabled = submissionInFlight || !ready;
    if (!ready && addToCartButton) addToCartButton.disabled = true;
    root.dataset.artworkPendingCount = String(active.length);
    root.dataset.artworkQualityState = failed ? 'error' : ready ? 'ready' : 'loading';
    if (!artworkQuality) return;
    artworkQuality.hidden = ready && !hasArtwork();
    artworkQuality.dataset.state = root.dataset.artworkQualityState;
    if (artworkRetry) artworkRetry.hidden = !failed;
    if (artworkQualityText) {
      const progress = loading.length === 1 && loading[0].progress ? ` ${loading[0].progress}` : '';
      artworkQualityText.textContent = qualityText(failed ? 'failedMessage' : ready ? 'readyMessage' : 'loadingMessage') + progress;
    }
  };
  let artworkTasks = new Map();
  const applyLibraryOriginal = (image, element, file, signal) => new Promise((resolve, reject) => {
    const cleanup = () => { pendingPixelSwaps.delete(run); signal.removeEventListener('abort', abort); };
    const abort = () => { cleanup(); resolve(); };
    const run = () => {
      cleanup();
      if (signal.aborted || libraryRequest.signal.aborted || !fabricCanvas.getObjects().includes(image)) { resolve(); return; }
      try {
        replaceArtworkPixels(image, element);
        sourceImageStore.set(image.sourceImageId, { id: image.sourceImageId, file, preview: false });
        image.originalFileName = file.name;
        fabricCanvas.requestRenderAll();
        updateUploadLimitStatus();
        resetConfirmation();
        updateTexture();
        root.dataset.artworkOriginalSize = `${element.naturalWidth}x${element.naturalHeight}`;
        root.dataset.artworkFinalPosition = `${Math.round(image.left)},${Math.round(image.top)}`;
        root.dataset.artworkLoadMs = String(Math.round(performance.now() - (image.mangoLoadStarted || performance.now())));
        resolve();
      } catch (error) { reject(error); }
    };
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) abort();
    else if (canvasInteractionActive) pendingPixelSwaps.add(run);
    else run();
  });
  const artworkUpgrades = createArtworkUpgradeManager({
    signal: libraryRequest.signal,
    isCurrent: (image) => fabricCanvas.getObjects().includes(image),
    load: (request, signal, progress) => loadLibraryPrint(request.id, request.url, (received, total) => {
      progress(total ? `${Math.min(100, Math.round(received / total * 100))}%` : `${(received / 1048576).toFixed(1)} MB`);
    }, (delivery, reason) => { root.dataset.artworkDelivery = delivery; if (reason) root.dataset.artworkDeliveryError = reason; }, signal),
    prepare: (blob, request) => prepareLibraryArtworkFile(blob, `${request.id}.${blob.type === 'image/jpeg' ? 'jpg' : 'png'}`, request.usage),
    decode: decodeArtworkFile,
    apply: applyLibraryOriginal,
    changed: refreshArtworkQuality
  });
  fabricCanvas.on('mouse:down', () => { canvasInteractionActive = true; });
  fabricCanvas.on('mouse:up', finishCanvasInteraction);
  window.addEventListener('blur', finishCanvasInteraction, { signal: libraryRequest.signal });
  fabricCanvas.on('object:removed', ({ target }) => artworkUpgrades.remove(target));
  artworkRetry?.addEventListener('click', () => artworkUpgrades.retry(), { signal: libraryRequest.signal });

  const getLibraryPreview = async (button) => {
    const image = button.querySelector('img');
    if (image?.complete && image.naturalWidth) {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth; canvas.height = image.naturalHeight;
      try {
        canvas.getContext('2d').drawImage(image, 0, 0);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (blob) return blob;
      } catch {} finally { canvas.width = 0; canvas.height = 0; }
    }
    let timer;
    try {
      return await Promise.race([
        libraryThumbnails.load(button.dataset.mangoThumbnailUrl, button.dataset.mangoThumbnailRevision),
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Preview unavailable')), 2000); })
      ]);
    } finally { clearTimeout(timer); }
  };

  const addLogoImage = (file, options = {}) => new Promise((resolve) => {
    if (!file || !/^image\/(png|jpeg)$/.test(file.type)) {
      showWarning('Please upload a PNG or JPG image.');
      resolve(null);
      return;
    }
    if (file.size > maxSingleImageBytes) {
      showWarning(`A single image cannot exceed ${formatFileSize(maxSingleImageBytes)}.`);
      resolve(null);
      return;
    }
    cleanupUnusedSourceImages();
    const previous = options.replaceKind
      ? fabricCanvas.getObjects().find((object) => object.mangoArtworkKind === options.replaceKind)
      : null;
    if (sourceImageStore.size - (previous ? 1 : 0) >= maxImages) {
      showWarning(`One design can use up to ${maxImages} images.`);
      resolve(null);
      return;
    }

    const insertionTemplate = templateConfig;
    const reader = new FileReader();
    reader.onload = () => {
      const htmlImage = new Image();
      htmlImage.onload = () => {
        if (libraryRequest.signal.aborted || insertionTemplate !== templateConfig) { resolve(null); return; }
        if (previous) {
          fabricCanvas.remove(previous);
          cleanupUnusedSourceImages();
        }
        const sourceImageId = `src-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        sourceImageStore.set(sourceImageId, {
          id: sourceImageId,
          file,
          preview: Boolean(options.preview)
        });
        updateUploadLimitStatus();
        const image = new fabric.Image(htmlImage, {
          left: textureWidth * 0.28,
          top: textureHeight * 0.32,
          originX: 'center',
          originY: 'center',
          padding: 8,
          selectable: true,
          evented: true,
          sourceImageId,
          originalFileName: file.name,
          mangoArtworkKind: options.kind || 'upload',
          mangoLibraryId: options.libraryId || '',
          mangoPrintState: options.preview ? 'loading' : 'ready',
          personalizationName: options.name || '',
          personalizationNumber: options.number || ''
        });

        const maxWidth = textureWidth * 0.34;
        const maxHeight = textureHeight * 0.42;
        const canvasRatio = textureWidth / textureHeight;
        const imageRatio = htmlImage.naturalWidth / htmlImage.naturalHeight;
        const isCanvasRatio = Math.abs(imageRatio - canvasRatio) / canvasRatio <= 0.01;

        if (options.kind === 'library-element') {
          const pieceCount = fabricCanvas.getObjects().filter((object) => object.mangoArtworkKind === 'library-element').length;
          image.set({
            left: textureWidth * (pieceCount % 2 ? 0.72 : 0.28),
            top: textureHeight * (Math.floor(pieceCount / 2) % 2 ? 0.74 : 0.3)
          });
        }

        if (options.kind === 'personalization') {
          image.set({ left: getCenterX(), top: getCenterY() });
          image.scale(Math.min(textureWidth * 0.67 / htmlImage.naturalWidth, textureHeight * 0.3 / htmlImage.naturalHeight));
        } else if (options.kind !== 'library-element' && (options.layout === 'full-wrap' || isCanvasRatio)) {
          image.set({
            left: getCenterX(),
            top: getCenterY(),
            scaleX: textureWidth / htmlImage.naturalWidth,
            scaleY: textureHeight / htmlImage.naturalHeight
          });
        } else {
          const scale = Math.min(maxWidth / image.width, maxHeight / image.height, options.kind === 'library-element' && !options.preview ? 1 : Infinity);
          image.scale(scale);
        }

        fabricCanvas.add(image);
        if (options.kind === 'library' && options.layout === 'full-wrap') fabricCanvas.sendToBack(image);
        setWorkflowStep(section, 3);
        fabricCanvas.setActiveObject(image);
        fabricCanvas.requestRenderAll();
        root.dataset.fabricLastUpload = `${htmlImage.naturalWidth}x${htmlImage.naturalHeight}`;
        root.dataset.fabricLastObject = `${Math.round(image.getScaledWidth())}x${Math.round(image.getScaledHeight())}`;
        root.dataset.fabricLastPosition = `${Math.round(image.left)},${Math.round(image.top)}`;
        showWarning(options.kind === 'personalization'
          ? 'Name and number added. Review their position in the 3D preview before confirming.'
          : (options.kind === 'library-element' ? libraryRoot.dataset.mangoMixAdded : (isCanvasRatio || options.layout === 'full-wrap' ? 'The image ratio matches the custom canvas and has been fitted to the full canvas.' : 'The image has been added to the canvas. You can drag and scale it; it will snap near the green center guides.')));
        updateTexture();
        resolve(image);
      };
      htmlImage.onerror = () => {
        showWarning('Image reading failed. Please try another PNG or JPG.');
        resolve(null);
      };
      htmlImage.src = reader.result;
    };
    reader.onerror = () => {
      showWarning('Image reading failed. Please try another PNG or JPG.');
      resolve(null);
    };
    reader.readAsDataURL(file);
  });

  const addLibraryArtwork = async (event) => {
    const button = event.target.closest('[data-mango-library-artwork]');
    const url = button?.dataset.mangoLibraryUrl;
    if (!url || button.disabled || libraryTransferBusy) return;
    libraryTransferBusy = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const transferStatus = (message, failed = false) => {
      if (!libraryTransfer) return;
      libraryTransfer.hidden = false;
      libraryTransfer.textContent = message;
      libraryTransfer.dataset.error = String(failed);
    };
    transferStatus(qualityText('openingMessage'));
    const started = performance.now();
    const request = { id: button.dataset.mangoLibraryId || 'library-design', url, usage: button.dataset.mangoLibraryUsage };
    try {
      let blob;
      let preview = false;
      try { blob = await getLibraryPreview(button); preview = true; } catch {}
      if (!blob) blob = await loadLibraryPrint(request.id, url, (received, total) => {
        transferStatus(`${qualityText('openingMessage')} ${total ? Math.round(received / total * 100) + '%' : (received / 1048576).toFixed(1) + ' MB'}`);
      });
      if (libraryRequest.signal.aborted) return;
      const file = await prepareLibraryArtworkFile(blob, `${request.id}.png`, request.usage, preview);
      const image = await addLogoImage(file, { ...getLibraryArtworkOptions(request.usage, request.id), preview });
      if (!image) throw new Error(qualityText('addFailedMessage'));
      image.mangoLoadStarted = started;
      root.dataset.artworkPreviewMs = String(Math.round(performance.now() - started));
      if (libraryTransfer) libraryTransfer.hidden = true;
      libraryDialog?.close();
      requestAnimationFrame(() => {
        artworkQuality?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      });
      if (preview) void artworkUpgrades.start(image, request);
      else refreshArtworkQuality();
    } catch (error) {
      if (!libraryRequest.signal.aborted) {
        const message = error.message || qualityText('addFailedMessage');
        transferStatus(message, true);
        showWarning(message);
      }
    } finally {
      libraryTransferBusy = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  const setupArtworkLibrary = async () => {
    const catalogUrl = libraryRoot?.dataset.mangoCatalogUrl?.trim();
    if (!catalogUrl || !libraryGrid || !libraryStatus) return;
    const thumbnails = libraryThumbnails;
    const objectUrls = new Set();
    const imageSources = new WeakMap();
    let activeRequest = null;
    let selectedCategory = '';
    let currentPage = 0;
    let total = 0;
    let requestSequence = 0;
    let previewItems = null;
    let searchTimer = null;
    let displayedItems = [];
    let categoryNames = [];
    const loadThumbnail = async (image) => {
      const source = imageSources.get(image);
      if (!source) return;
      imageSources.delete(image);
      try {
        const blob = await thumbnails.load(source.url, source.revision);
        if (libraryRequest.signal.aborted || !image.isConnected) return;
        const url = URL.createObjectURL(blob);
        objectUrls.add(url);
        image.src = url;
      } catch (error) {
        if (error.name !== 'AbortError' && image.isConnected && !libraryRequest.signal.aborted) image.src = source.url;
      }
    };
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          void loadThumbnail(entry.target);
        }
      });
    }, { rootMargin: '300px' }) : null;
    const clearThumbnails = () => {
      observer?.disconnect();
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
      objectUrls.clear();
      libraryGrid.replaceChildren();
    };
    const setCategory = (value) => {
      selectedCategory = value;
      libraryCategories?.querySelectorAll('[data-mango-library-category]').forEach((button) => {
        button.setAttribute('aria-pressed', String((button.dataset.mangoLibraryCategory || '') === value));
      });
    };
    const setCategories = (categories) => {
      if (JSON.stringify(categories) === JSON.stringify(categoryNames)) return;
      categoryNames = categories;
      libraryCategories?.querySelectorAll('[data-mango-library-category]').forEach((button) => {
        if (button.dataset.mangoLibraryCategory) button.remove();
      });
      categories.forEach((category) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.mangoLibraryCategory = category;
        button.textContent = category.replaceAll(' / ', ' · ');
        libraryCategories?.append(button);
      });
      setCategory(categories.includes(selectedCategory) ? selectedCategory : '');
    };
    const appendItems = (items, baseUrl) => {
      items.forEach((item) => {
        try {
          const printUrl = new URL(item.print_url, baseUrl);
          const imageUrl = new URL(item.thumbnail_url || item.print_url, baseUrl);
          if (!item.id || !item.title || printUrl.origin !== baseUrl.origin || imageUrl.origin !== baseUrl.origin) return;
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.mangoLibraryArtwork = '';
          button.dataset.mangoLibraryId = String(item.id);
          button.dataset.mangoLibraryUrl = printUrl.href;
          const tags = getArtworkTags(item.category);
          button.dataset.mangoLibraryUsage = tags.usage;
          const image = document.createElement('img');
          image.crossOrigin = 'anonymous';
          image.alt = '';
          image.loading = 'lazy';
          button.dataset.mangoThumbnailUrl = imageUrl.href;
          button.dataset.mangoThumbnailRevision = JSON.stringify([item.id, item.title, item.category, item.updatedAt || item.version || '', printUrl.href]);
          imageSources.set(image, { url: imageUrl.href, revision: button.dataset.mangoThumbnailRevision });
          const label = document.createElement('span');
          label.textContent = String(item.title);
          button.append(image, label);
          const badges = document.createElement('span');
          badges.className = 'mango-artwork-library__tags';
          if (tags.usage) {
            const badge = document.createElement('span');
            badge.className = `mango-artwork-library__tag mango-artwork-library__tag--${tags.usage}`;
            badge.textContent = tags.usage === 'mix-match' ? libraryRoot.dataset.mangoMixLabel : libraryRoot.dataset.mangoWrapLabel;
            badges.append(badge);
          }
          tags.topics.forEach((topic) => {
            const badge = document.createElement('span');
            badge.className = 'mango-artwork-library__tag';
            badge.textContent = topic;
            badges.append(badge);
          });
          button.append(badges);
          if (tags.usage) {
            const hint = document.createElement('span');
            hint.className = 'mango-artwork-library__hint';
            hint.textContent = tags.usage === 'mix-match' ? libraryRoot.dataset.mangoMixHint : libraryRoot.dataset.mangoWrapHint;
            button.append(hint);
          }
          libraryGrid.append(button);
          if (observer) observer.observe(image);
          else void loadThumbnail(image);
        } catch {}
      });
    };
    const fetchCatalog = async (page, catalogLocation, signal, search, category) => {
      const url = new URL(catalogLocation.href);
      url.searchParams.set('page', String(page));
      if (search) url.searchParams.set('q', search);
      else url.searchParams.delete('q');
      if (category) url.searchParams.set('category', category);
      else url.searchParams.delete('category');
      const response = await fetch(url.href, { credentials: 'omit', cache: 'no-cache', signal });
      if (!response.ok) throw new Error('Designs are temporarily unavailable. You can still upload your own image.');
      const data = await response.json();
      if (![1, 2].includes(data.version) || !Array.isArray(data.items)) throw new Error('The artwork catalog needs an update.');
      return data;
    };
    const fetchCategories = async (catalogLocation, signal) => {
      const url = new URL(catalogLocation.href);
      url.pathname = url.pathname.replace(/catalog\.json$/, 'categories.json');
      url.search = '';
      try {
        const response = await fetch(url.href, { credentials: 'omit', cache: 'no-cache', signal });
        if (!response.ok) return null;
        const data = await response.json();
        return Array.isArray(data.categories) ? data.categories.map((item) => String(item.name || '')).filter(Boolean) : null;
      } catch { return null; }
    };
    const renderPage = async (page, catalogLocation, { refresh = false, catalog = null } = {}) => {
      activeRequest?.abort();
      activeRequest = new AbortController();
      const signal = activeRequest.signal;
      const sequence = ++requestSequence;
      const search = (librarySearch?.value || '').trim();
      const category = selectedCategory;
      const pageCount = refresh ? Math.max(1, currentPage) : 1;
      if (!refresh || !displayedItems.length) libraryStatus.textContent = 'Loading designs…';
      if (libraryMore) libraryMore.hidden = true;
      try {
        const categoriesPromise = (refresh || currentPage === 0) && !previewItems ? fetchCategories(catalogLocation, signal) : Promise.resolve(null);
        const pagesPromise = previewItems && !refresh
          ? Promise.resolve([{ version: 1, items: previewItems }])
          : Promise.all(Array.from({ length: pageCount }, (_, index) => {
            if (catalog && index === 0) return catalog;
            return fetchCatalog(refresh ? index + 1 : page, catalogLocation, signal, search, category);
          }));
        const [pages, categories] = await Promise.all([pagesPromise, categoriesPromise]);
        if (signal.aborted || sequence !== requestSequence) return;
        let items;
        let nextTotal;
        if (pages[0].version === 1) {
          previewItems = pages[0].items;
          const names = [...new Set(previewItems.map((item) => String(item.category || '')).filter(Boolean))].sort((a, b) => a.localeCompare(b));
          setCategories(names);
          const matched = previewItems.filter((item) => (!selectedCategory || item.category === selectedCategory) && (!search || `${item.title} ${item.category}`.toLocaleLowerCase().includes(search.toLocaleLowerCase())));
          nextTotal = matched.length;
          items = refresh ? matched.slice(0, pageCount * 12) : matched.slice((page - 1) * 12, page * 12);
        } else {
          if (categories) setCategories(categories);
          if (category !== selectedCategory) {
            await renderPage(1, catalogLocation);
            return;
          }
          nextTotal = Number(pages[0].total) || 0;
          items = pages.flatMap((data) => data.items);
        }
        const nextItems = [...new Map((refresh || page === 1 ? items : [...displayedItems, ...items]).map((item) => [item.id, item])).values()];
        if (JSON.stringify(nextItems) !== JSON.stringify(displayedItems)) {
          if (!refresh && page > 1) appendItems(nextItems.slice(displayedItems.length), catalogLocation);
          else {
            clearThumbnails();
            appendItems(nextItems, catalogLocation);
          }
          displayedItems = nextItems;
        }
        total = nextTotal;
        currentPage = refresh ? Math.min(pageCount, Math.max(1, Math.ceil(total / 12))) : page;
        libraryRoot.hidden = false;
        libraryGrid.hidden = libraryGrid.childElementCount === 0;
        libraryStatus.textContent = total ? `Showing ${libraryGrid.childElementCount} of ${total} designs.` : 'No designs match this search.';
        if (libraryMore) libraryMore.hidden = libraryGrid.childElementCount >= total;
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === requestSequence) {
          libraryStatus.textContent = displayedItems.length ? 'Could not check for new designs. Close and reopen to try again.' : error.message || 'Designs are temporarily unavailable.';
          if (libraryMore) libraryMore.hidden = libraryGrid.childElementCount >= total;
        }
      }
    };
    try {
      const catalogLocation = new URL(catalogUrl, window.location.href);
      if (!['https:', 'http:'].includes(catalogLocation.protocol)) throw new Error('The artwork catalog URL is invalid.');
      const catalog = await fetchCatalog(1, catalogLocation, libraryRequest.signal, '', '');
      if (libraryRequest.signal.aborted) return;
      libraryOpen?.addEventListener('click', () => {
        libraryDialog?.showModal();
        librarySearch?.focus();
        void renderPage(1, catalogLocation, { refresh: true });
      }, { signal: libraryRequest.signal });
      libraryClose?.addEventListener('click', () => libraryDialog?.close(), { signal: libraryRequest.signal });
      libraryDialog?.addEventListener('click', (event) => { if (event.target === libraryDialog) libraryDialog.close(); }, { signal: libraryRequest.signal });
      libraryDialog?.addEventListener('close', () => libraryOpen?.focus(), { signal: libraryRequest.signal });
      libraryRoot.querySelector('[data-mango-library-upload]')?.addEventListener('click', () => { libraryDialog?.close(); uploadInput?.click(); }, { signal: libraryRequest.signal });
      libraryRoot.querySelector('[data-mango-library-print]')?.addEventListener('click', () => { libraryDialog?.close(); }, { signal: libraryRequest.signal });
      librarySearch?.addEventListener('input', () => {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => { void renderPage(1, catalogLocation); }, 250);
      }, { signal: libraryRequest.signal });
      libraryCategories?.addEventListener('click', (event) => {
        const categoryButton = event.target.closest('[data-mango-library-category]');
        if (!categoryButton) return;
        setCategory(categoryButton.dataset.mangoLibraryCategory || '');
        void renderPage(1, catalogLocation);
      }, { signal: libraryRequest.signal });
      libraryMore?.addEventListener('click', () => { void renderPage(currentPage + 1, catalogLocation); }, { signal: libraryRequest.signal });
      libraryRequest.signal.addEventListener('abort', () => {
        activeRequest?.abort();
        clearTimeout(searchTimer);
        clearThumbnails();
      }, { once: true });
      await renderPage(1, catalogLocation, { catalog });
    } catch (error) {
      observer?.disconnect();
      if (error.name !== 'AbortError') libraryRoot.hidden = true;
    }
  };

  const addNameAndNumber = async () => {
    const name = (personalNameInput?.value || '').trim().replace(/\s+/g, ' ').slice(0, 24);
    const number = (personalNumberInput?.value || '').trim().toUpperCase().slice(0, 6);
    if (!name && !number) {
      const previous = fabricCanvas.getObjects().find((object) => object.mangoArtworkKind === 'personalization');
      if (previous) {
        fabricCanvas.remove(previous);
        cleanupUnusedSourceImages();
        updateTexture();
        resetConfirmation('Name and number removed. Please confirm your design again.');
        showWarning('Name and number removed from the bottle.');
      } else {
        showWarning('Enter a name or number to add to your bottle.');
      }
      return;
    }
    if (addPersonalizationButton) addPersonalizationButton.disabled = true;
    try {
      await document.fonts.ready;
      const printCanvas = document.createElement('canvas');
      printCanvas.width = 1800;
      printCanvas.height = 900;
      const context = printCanvas.getContext('2d');
      if (!context) throw new Error('The name preview could not be prepared in this browser.');
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillStyle = '#ffffff';
      context.shadowColor = 'rgba(0,0,0,.35)';
      context.shadowBlur = 16;
      const sizeFor = (value, maximum) => {
        let size = maximum;
        do {
          context.font = `900 ${size}px Arial, sans-serif`;
          if (context.measureText(value).width <= 1600) return size;
          size -= 12;
        } while (size > 90);
        return 90;
      };
      if (number) {
        context.font = `900 ${sizeFor(number, 420)}px Arial, sans-serif`;
        context.fillText(number, 900, name ? 330 : 450);
      }
      if (name) {
        context.font = `900 ${sizeFor(name.toUpperCase(), number ? 170 : 265)}px Arial, sans-serif`;
        context.fillText(name.toUpperCase(), 900, number ? 675 : 450);
      }
      const blob = await new Promise((resolve) => printCanvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('The name preview could not be saved. Please try again.');
      const file = new File([blob], 'mangorace-name-number.png', { type: 'image/png' });
      await addLogoImage(file, { kind: 'personalization', replaceKind: 'personalization', name, number });
    } catch (error) {
      showWarning(error.message || 'The name preview could not be prepared.');
    } finally {
      if (addPersonalizationButton) addPersonalizationButton.disabled = false;
    }
  };

  const onUploadInputChange = (event) => {
    const files = [...(event.target.files || [])];
    if (canAcceptUploadFiles(files)) {
      files.forEach((file) => addLogoImage(file));
    }
    event.target.value = '';
  };

  const onEditorKeydown = (event) => {
    const activeTag = document.activeElement?.tagName?.toLowerCase();
    if (['input', 'select', 'textarea'].includes(activeTag)) return;
    if ((event.key === 'Delete' || event.key === 'Backspace') && root.contains(document.activeElement)) {
      event.preventDefault();
      removeActiveObjects();
    }
  };

  const onGuideModeChange = () => {
    showSetupOrArtworkStep();
    resetConfirmation('Guide mode changed. Please confirm the custom design again before adding to cart.');
    renderGuideOverlay();
    showWarning('Guide mode switched. Artwork centers will snap near the green guide lines.');
  };

  const onFinishModeChange = () => {
    showSetupOrArtworkStep();
    resetConfirmation('Surface finish changed. Please confirm the custom design again before adding to cart.');
    setFinishMode();
  };

  const onCapOptionsClick = (event) => {
    const option = event.target.closest('[data-mango-cap-option]');
    if (!option || !capOptions.contains(option)) return;
    root.querySelectorAll('[data-mango-cap-option]').forEach((item) => {
      item.setAttribute('aria-pressed', String(item === option));
    });
    const capOption = getSelectedCapOption();
    showSetupOrArtworkStep();
    resetConfirmation(`Bottle cap changed to ${capOption.label}. Please confirm the custom design again before adding to cart.`);
    setCapOption();
  };

  const onMouthpieceOptionsClick = (event) => {
    const option = event.target.closest('[data-mango-mouthpiece-option]');
    if (!option || !mouthpieceOptions.contains(option)) return;
    root.querySelectorAll('[data-mango-mouthpiece-option]').forEach((item) => {
      item.setAttribute('aria-pressed', String(item === option));
    });
    const mouthpieceOption = getSelectedMouthpieceOption();
    showSetupOrArtworkStep();
    resetConfirmation(`Mouthpiece changed to ${mouthpieceOption.label}. Please confirm the custom design again before adding to cart.`);
    setMouthpieceOption();
  };

  const onIpAgreementChange = () => {
    if (ipAgreement.checked && hasArtwork()) {
      setWorkflowStep(section, 4);
    } else if (!ipAgreement.checked) {
      resetConfirmation('Please confirm the artwork authorization again before continuing.');
      showSetupOrArtworkStep();
    }
  };

  const onQuantityChange = () => {
    if (hasArtwork()) setWorkflowStep(section, 4);
  };

  const onPersonalInputChange = () => {
    resetConfirmation('Name or number changed. Preview the updated design before confirming.');
  };

  const onSnapshotReady = (event) => {
    latestSnapshotDataUrl = event.detail?.dataUrl || '';
    const dataUrl = event.detail?.dataUrl || '';
    const bytes = dataUrl ? Math.round((dataUrl.length * 3) / 4) : 0;
    root.dataset.mangoSnapshotState = bytes ? 'ready' : 'unavailable';
    root.dataset.mangoSnapshotBytes = String(bytes);
  };

  uploadInput?.addEventListener('change', onUploadInputChange);
  libraryGrid?.addEventListener('click', addLibraryArtwork);
  void setupArtworkLibrary();
  addPersonalizationButton?.addEventListener('click', addNameAndNumber);
  personalNameInput?.addEventListener('input', onPersonalInputChange);
  personalNumberInput?.addEventListener('input', onPersonalInputChange);
  deleteButton?.addEventListener('click', removeActiveObjects);
  document.addEventListener('keydown', onEditorKeydown);
  guideModeSelect?.addEventListener('change', onGuideModeChange);
  root.addEventListener('mango:template-changed', applyEditorTemplate);
  finishModeSelect?.addEventListener('change', onFinishModeChange);
  capOptions?.addEventListener('click', onCapOptionsClick);
  mouthpieceOptions?.addEventListener('click', onMouthpieceOptionsClick);
  ipAgreement?.addEventListener('change', onIpAgreementChange);
  quantityInput?.addEventListener('change', onQuantityChange);
  confirmButton?.addEventListener('click', onConfirmDesignClick);
  addToCartButton?.addEventListener('click', addToCart);
  root.addEventListener('mango:3d-snapshot-ready', onSnapshotReady);

  fitCanvasToStage();
  renderGuideOverlay();
  applyRequestedFinish();
  setFinishMode();
  setCapOption();
  setMouthpieceOption();
  updateUploadLimitStatus();
  window.addEventListener('resize', fitCanvasToStage);
  if (stage && 'ResizeObserver' in window) {
    stageResizeObserver = new ResizeObserver((entries) => {
      const nextWidth = entries[0]?.contentRect?.width || 0;
      if (nextWidth <= 0 || Math.abs(nextWidth - observedStageWidth) < 1) return;
      observedStageWidth = nextWidth;
      fitCanvasToStage();
    });
    stageResizeObserver.observe(stage);
  }
  canvasFitFrame = window.requestAnimationFrame(() => {
    canvasFitFrame = 0;
    fitCanvasToStage();
  });
  updateTexture();
  void (async () => {
    try {
      const restoredRemoteDraft = await restoreRemoteDraftIfRequested();
      if (!restoredRemoteDraft) await restoreLocalDraftIfAvailable();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Saved design loading failed. Please try again later.';
      showWarning(message);
      if (orderStatus) orderStatus.textContent = message;
    }
  })();

  const editor = {
    canvas: fabricCanvas,
    element: canvasEl,
    textureElement: textureCanvas,
    updateTexture,
    destroy() {
      window.removeEventListener('resize', fitCanvasToStage);
      stageResizeObserver?.disconnect();
      fabricCanvas.upperCanvasEl.removeEventListener('pointerdown', onCanvasPointerDown);
      fabricCanvas.upperCanvasEl.removeEventListener('touchstart', beginPinchGesture, true);
      fabricCanvas.upperCanvasEl.removeEventListener('touchmove', movePinchGesture, true);
      fabricCanvas.upperCanvasEl.removeEventListener('touchend', endPinchGesture, true);
      fabricCanvas.upperCanvasEl.removeEventListener('touchcancel', endPinchGesture, true);
      uploadInput?.removeEventListener('change', onUploadInputChange);
      libraryRequest.abort();
      libraryGrid?.removeEventListener('click', addLibraryArtwork);
      addPersonalizationButton?.removeEventListener('click', addNameAndNumber);
      personalNameInput?.removeEventListener('input', onPersonalInputChange);
      personalNumberInput?.removeEventListener('input', onPersonalInputChange);
      deleteButton?.removeEventListener('click', removeActiveObjects);
      document.removeEventListener('keydown', onEditorKeydown);
      guideModeSelect?.removeEventListener('change', onGuideModeChange);
      root.removeEventListener('mango:template-changed', applyEditorTemplate);
      finishModeSelect?.removeEventListener('change', onFinishModeChange);
      capOptions?.removeEventListener('click', onCapOptionsClick);
      mouthpieceOptions?.removeEventListener('click', onMouthpieceOptionsClick);
      ipAgreement?.removeEventListener('change', onIpAgreementChange);
      quantityInput?.removeEventListener('change', onQuantityChange);
      confirmButton?.removeEventListener('click', onConfirmDesignClick);
      addToCartButton?.removeEventListener('click', addToCart);
      root.removeEventListener('mango:3d-snapshot-ready', onSnapshotReady);
      if (canvasFitFrame) window.cancelAnimationFrame(canvasFitFrame);
      if (textureUpdateFrame) window.cancelAnimationFrame(textureUpdateFrame);
      fabricCanvas.dispose();
    }
  };
  editorBySection.set(root, editor);
  return editor;
}

function bindEditorTexture(root, model, editor, state) {
  if (!editor) return;

  const bodyMeshName = root.dataset.bodyMeshName || 'Bottle_Body';
  const customAreaName = root.dataset.customAreaName || 'Custom_Area';
  const targetMesh = findMeshByName(model, [bodyMeshName, customAreaName]);

  if (!targetMesh) {
    setStatus(root, `Mesh not found: ${bodyMeshName} or ${customAreaName}. Please check the internal .glb mesh names.`);
    return;
  }

  const sourceMaterials = Array.isArray(targetMesh.material) ? targetMesh.material : [targetMesh.material];
  const baseMaterials = sourceMaterials.map((material) => (
    material?.clone ? material.clone() : new THREE.MeshStandardMaterial({ color: 0xffffff })
  ));
  const texture = new THREE.CanvasTexture(editor.textureElement);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.flipY = false;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.anisotropy = 8;
  const requestedUvChannel = Number.parseInt(root.dataset.customUvChannel || '1', 10);
  const hasSecondUv = Boolean(targetMesh.geometry.getAttribute('uv1'));
  texture.channel = requestedUvChannel === 1 && hasSecondUv ? 1 : 0;
  const configuredShadowLift = Number.parseFloat(root.dataset.artworkShadowLift || '');
  const artworkShadowLift = Math.min(0.15, Math.max(0, (
    Number.isFinite(configuredShadowLift) ? configuredShadowLift : 8
  ) / 100));
  root.dataset.appliedArtworkShadowLift = artworkShadowLift.toFixed(2);
  const textureRotation = Number.parseFloat(root.dataset.textureRotation || '');
  if (Number.isFinite(textureRotation)) {
    texture.center.set(0.5, 0.5);
    texture.rotation = THREE.MathUtils.degToRad(textureRotation);
  }
  const overlayMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    map: texture,
    emissive: 0xffffff,
    emissiveMap: texture,
    emissiveIntensity: artworkShadowLift,
    transparent: true,
    alphaTest: 0.01,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1
  });
  overlayMaterial.userData.mangoArtworkOverlay = true;
  overlayMaterial.dithering = true;

  targetMesh.material = Array.isArray(targetMesh.material) ? baseMaterials : baseMaterials[0];

  const overlayMesh = new THREE.Mesh(targetMesh.geometry, overlayMaterial);
  overlayMesh.name = `${targetMesh.name}_CustomizationOverlay`;
  overlayMesh.position.copy(targetMesh.position);
  overlayMesh.rotation.copy(targetMesh.rotation);
  overlayMesh.scale.copy(targetMesh.scale).multiplyScalar(1.001);
  overlayMesh.renderOrder = 10;
  targetMesh.parent?.add(overlayMesh);

  state.targetMesh = targetMesh;
  state.targetMaterial = [...baseMaterials, overlayMaterial];
  state.overlayMesh = overlayMesh;
  state.texture = texture;
  root.dataset.textureTarget = targetMesh.name;
  root.dataset.textureUvChannel = String(texture.channel);

  const customizer = editor.element.closest('[data-mango-customizer]');
  if (state.textureUpdateListener && state.textureUpdateRoot) {
    state.textureUpdateRoot.removeEventListener('mango:fabric-texture-updated', state.textureUpdateListener);
  }
  const onFabricTextureUpdated = () => {
    texture.needsUpdate = true;
    overlayMaterial.needsUpdate = true;
    state.requestRender?.();
  };
  customizer?.addEventListener('mango:fabric-texture-updated', onFabricTextureUpdated);
  state.textureUpdateListener = onFabricTextureUpdated;
  state.textureUpdateRoot = customizer;

  texture.needsUpdate = true;
  const finish = editor.element.closest('[data-mango-customizer]')?.querySelector('[data-mango-finish-mode]')?.value || 'matte';
  applyBottleFinish(state.targetMaterial, finish);
}

function initAllViewers() {
  document.querySelectorAll('.mango-3d-service').forEach((section) => {
    initTemplatePicker(section);
    initCustomizerTour(section);
  });
  document.querySelectorAll('[data-mango-3d-viewer]').forEach(initViewer);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllViewers);
} else {
  initAllViewers();
}

document.addEventListener('shopify:section:load', initAllViewers);
document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target?.matches?.('.mango-3d-service')
    ? event.target
    : event.target?.querySelector?.('.mango-3d-service');
  if (!section) return;

  customizerTours.get(section)?.destroy();
  initializedTemplatePickers.delete(section);

  const viewerRoot = section.querySelector('[data-mango-3d-viewer]');
  viewers.get(viewerRoot)?.destroy();
  viewers.delete(viewerRoot);

  const customizerRoot = section.querySelector('[data-mango-customizer]');
  editorBySection.get(customizerRoot)?.destroy();
  editorBySection.delete(customizerRoot);
});
